import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { isAndroid, isIOS } from 'react-device-detect'
import {
    Check,
    ChevronDown,
    ChevronUp,
    Download,
    Film,
    GripVertical,
    History as HistoryIcon,
    ListMusic,
    Megaphone,
    MessageCircle,
    Music,
    Music2,
    Pause,
    Play,
    Plus,
    Radio,
    RadioTower,
    Repeat,
    Shuffle,
    SkipBack,
    SkipForward,
    Volume2,
    VolumeX,
    X,
} from 'lucide-react'
import {
    announceMediaDockCinemaExit,
    announceMediaDockHostedReturn,
    beginDockMediaHandoff,
    cancelMediaHandoffForVisibleSource,
    createMediaPlaybackOwnerId,
    createMediaTrackPlacementCache,
    findMountedOwnerForMediaKey,
    focusMediaDockTrack,
    getMediaDockHostedPlacement,
    getMediaDockSnapshot,
    getMediaHandoffInfo,
    isInlineMediaPlaybackHandoffPending,
    isMediaKeyPresentedInDock,
    getMediaOwnerPlaybackSnapshot,
    getMediaPlaybackState,
    getMediaTrack,
    getMediaTrackOriginPlacement,
    getMediaTrackOriginRect,
    getVisibleMediaTrackOrigin,
    hydrateMediaDockHistory,
    isElementUserVisible,
    isMediaTrackInPlaylist,
    loadMoreMediaDockItems,
    moveMediaTrackToPlaylistTop,
    pauseMediaKey,
    playMediaKey,
    registerMediaTrack,
    removeMediaTrackFromHistory,
    removeMediaTrackFromPlaylist,
    seekMediaKey,
    setMediaDockHostedChromeLayer,
    setMediaDockHostedPlacement,
    setMediaDockPresentedMediaKey,
    setMediaDockHostedSurfaceElement,
    setMediaKeyCaptionsEnabled,
    setMediaKeyPlaybackRate,
    setMediaKeyVolume,
    setMediaPlaybackState,
    subscribeToMediaDock,
    subscribeToMediaPlaybackState,
    syncMediaDockPlaylistFromServer,
    setMediaDockRadioActive,
} from 'state/mediaPlaybackCoordinator'
import { hostedMediaPlaybackEnabled } from 'lib/hostedMediaPlayback'
import perms from 'lib/perms'
import { getCinemaReturnPresentation, mediaDockCinemaEnabled } from 'lib/mediaDockCinema'
import useStore from 'state/knovStore'
import { treechatRadioEnabled } from 'lib/treechatRadio'
import { radioNowFeed, subscribeRadioNow } from 'state/radioNowFeed'
import {
    clearInlineRadioPlay,
    isInlineRadioKey,
    isInlineRadioPlaying,
} from './mediaDockRadioContext'
import { cacheAnswer, getCachedAnswer } from 'state/cache'
import toast from 'utils/toast'
import api from 'api/api'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import { incrementMediaDockQaCounter } from 'lib/mediaDockQaDiagnostics'
import { bottomControlsSlideY, resolveNavigationTop } from 'lib/bottomControlGeometry'
import { isMobileChromeHidden } from 'lib/mobileChromeAutoHide'

// Jump the dock's bottom-offset transition to its end, so a measurement taken
// now sees where the dock is going rather than where it was. (getAnimations
// flushes pending style first, so a just-written offset is included.)
const finishDockOffsetTransitions = element => {
    for (const animation of element?.getAnimations?.() || []) {
        if (animation.transitionProperty === 'bottom') animation.finish()
    }
}

// What the device draws over the bottom edge: the native shell reports the
// iOS home indicator as --native-safe-area-bottom and Android's navigation bar
// as --native-system-controls-bottom.
const readNativeSafeAreaBottom = () => {
    if (typeof window === 'undefined') return 0
    const style = window.getComputedStyle(document.documentElement)
    const inset = name => {
        const value = Number.parseFloat(style.getPropertyValue(name))
        return Number.isFinite(value) ? value : 0
    }
    return Math.max(inset('--native-safe-area-bottom'), inset('--native-system-controls-bottom'))
}
import { openCenter } from 'components/filters/useStreamFilters'
import WithTooltip from 'components/shared/WithTooltip'
import QuickUpvalueButton from 'components/votes/buttons/QuickUpvalueButton'
import UserIcon from 'components/users/UserIcon'
import TvMinimalPlayIcon from 'components/shared/icons/TvMinimalPlayIcon'
import { PayloadTileMenu } from 'components/answers/Answer/PayloadContainer'
import { isVideoFullscreenActive, toggleVideoFullscreen } from 'components/answers/videoFullscreen'
import { AudioArtworkTitle } from 'components/answers/FileEmbed/AudioArtworkCard'
import DockMediaElement from './DockMediaElement'
import MediaDockProgress from './MediaDockProgress'
import MediaDockStageTransport from './MediaDockStageTransport'
import {
    EMPTY_STAGE_RUNTIME_CONTROLS,
    findStageRuntimeOwner,
    readStageRuntimeControls,
    stageRuntimeControlsEqual,
} from './mediaDockStageControls'
import { addTrackToPlaylist, logMediaEvent, recordMediaPlay } from './mediaDockEvents'
import { getNextPlaylistTrack, getPreviousHistoryTrack } from './mediaDockNavigation'
import {
    fetchRadioPool,
    finishRadioNow,
    getRadioPoolOdds,
    liveRadioPosition,
    radioFinishRetryDelayMs,
} from './mediaDockRadio'
import {
    nowPlayingBroadcastEnabled,
    setNowPlayingBroadcastEnabled,
} from 'state/nowPlayingBroadcast'
import {
    constrainMobileMediaDockPosition,
    getExpandedMediaDockHeight,
    getMobileMediaDockAnchor,
} from './mediaDockLayout'
import { playInDockTab } from './mediaDockPlayInDock'
import { shouldAdoptCurrentMediaOnPlaylistOpen } from './mediaDockPlaylistOpen'
import {
    getTakeoverAfterAdoption,
    getTakeoverAfterHandoffCancellation,
    isDeferredDockTakeover,
    shouldAdoptOwnerLifecycleEvent,
    shouldWatchReleaseForAdoption,
} from './mediaDockReleaseAdoption'
import { getMediaDockPollIntervalMs } from './mediaDockPollPolicy'
import { resolveMediaDockChildThreadFilter } from './mediaDockThreadNavigation'
import {
    DOCK_YOUTUBE_PRELOAD_SYNC_INTERVAL_MS,
    shouldWaitForDockYouTubePreload,
} from './dockYouTubePlayer'
import {
    applyHostedAnchoredStyle,
    applyHostedPlacementStyle,
    buildHostedFlowClipPath,
    buildHostedPlacementStyle,
    computeAnchorResidual,
    computeHostedClipInset,
    HOSTED_ANCHORED_RESET_STYLE,
    HOSTED_CORNER_MORPH_ID,
    exitHostedSurfaceFullscreen,
    isHostedSurfaceFullscreen,
    moveHostedPresentation,
    replaceHostedAnchorElement,
    resolveHostedCornerRadii,
    resetHostedAnchoredStyle,
    restoreHostedPresentation,
    resolveHostedDetachReason,
    sameHostedCornerRadii,
    shouldKeepHostedPlacement,
    shouldKeepHostedRuntime,
    supportsAnchorPositioning,
    supportsStatePreservingMove,
} from './hostedMediaPlacement'
import {
    deriveMediaDockAnswerArtwork,
    deriveMediaDockAnswerTitle,
    getMediaDockTrackDisplayArtwork,
    getMediaDockTrackDisplayTitle,
} from './mediaDockTrackTitles'
import RadioChanceRow, { formatRadioChance } from './RadioChanceRow'
import { bsvOrdinalControlsEnabled } from 'components/panels/ordinalUpvalueFeature'
import styles from './media-dock.module.scss'
import audioStyles from 'components/answers/FileEmbed/audio.module.scss'
import useBsvDisplay from 'components/shared/useBsvDisplay'

const MORPH_DURATION_MS = 520
const LANDING_DURATION_MS = 460
const ADOPTION_WATCH_WINDOW_MS = 2500
const ADOPTION_WATCH_TICK_MS = 100
// After a playing hosted runtime leaves its post, how long a newly shown post
// with the same media (the thread a swipe or tap lands on, once it mounts)
// can still take it back from the corner.
const HOSTED_RETURN_WATCH_WINDOW_MS = 3000
const HOSTED_RETURN_WATCH_TICK_MS = 100
const HOSTED_RETURN_REASONS = new Set(['origin-offscreen', 'origin-lost', 'origin-hidden'])
const OVERLAY_HIDE_MS = 3000
// Cinema chrome fades this long after the pointer last moved while playing.
const CINEMA_CHROME_HIDE_MS = 2600
// Cinema's settings gear re-reads the runtime this often: a YouTube player
// reports its speeds only once ready, a native element its text tracks only
// once attached, and neither announces it.
const CINEMA_CONTROLS_POLL_MS = 1000
const CINEMA_FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
// How long leaving cinema waits for the post's media menu to mount again.
const CINEMA_FOCUS_RETURN_FRAMES = 10
const PLAYLIST_FOCUS_PENDING_MS = 12000
const DOCK_BOTTOM_MEASURE_INTERVAL_MS = 1000
const PLAYBACK_MODE_STORAGE_KEY = 'treechat:media-dock-playback:v1'

// A track's media menu trigger, wherever its chrome is drawn: the hosted
// chrome layer while the dock's runtime is parked over the post, otherwise
// the post's own media frame — the one cinema was opened from when it is still
// mounted, since the same post can be on screen in more than one panel.
const findMediaMenuTrigger = (mediaKey, originEl) => {
    if (!mediaKey || typeof document === 'undefined') return null
    const trigger = 'button[aria-haspopup="menu"]'
    const hostedChrome =
        getMediaDockHostedPlacement()?.mediaKey === mediaKey
            ? document.querySelector('[data-testid="media-dock-hosted-chrome"]')
            : null
    const origin =
        (originEl?.isConnected && originEl) ||
        Array.from(document.querySelectorAll('[data-media-track-origin]')).find(
            element => element.getAttribute('data-media-track-origin') === mediaKey,
        )
    return hostedChrome?.querySelector(trigger) || origin?.querySelector(trigger) || null
}

// A hosted runtime whose origin geometry is not known yet is parked out of
// view rather than flashing in the corner for a frame.
// A silent placement only watches for its post leaving the screen; the dock's
// own poll cadence is plenty.
const HOSTED_SILENT_POLL_MS = 250
// A real parent-child relationship is preferred where the browser can move an
// iframe without resetting it. CSS anchoring is the no-reload fallback.
const HOSTED_ANCHOR_POSITIONING = supportsAnchorPositioning()
const HOSTED_STATE_PRESERVING_MOVE = supportsStatePreservingMove()
const HOSTED_OFFSCREEN_STYLE = {
    transform: 'translate3d(-10000px, 0, 0)',
    width: '1px',
    height: '1px',
}
// Cinema fills the app window. Written inline so it also wins over any corner
// offset the morph wrote onto the element.
const CINEMA_ROOT_STYLE = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 'auto',
    maxWidth: 'none',
    height: 'auto',
}
const HOVER_VIDEO_CONTROLS_QUERY = '(hover: hover) and (pointer: fine)'
const MOBILE_DOCK_MARGIN_PX = 12
const MOBILE_DOCK_CONTROL_GAP_PX = 10
const MOBILE_DOCK_EXPANDED_ATTRIBUTE = 'data-mobile-media-dock-expanded'
const START_AT_BEGINNING_SOURCES = new Set(['autoplay', 'next', 'playlist'])
// A seek drag that ends off the slider dispatches its click on the tile
// under it; a tile click inside this window after a drag is not a navigation.
const STAGE_CLICK_AFTER_DRAG_GRACE_MS = 400

const canUseHoverVideoControls = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(HOVER_VIDEO_CONTROLS_QUERY).matches

const blurContainedActiveElement = event => {
    if (typeof document === 'undefined' || typeof HTMLElement === 'undefined') return
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement && event.currentTarget.contains(activeElement)) {
        activeElement.blur()
    }
}

// The Android app draws edge-to-edge under the status bar (like iOS), so the
// expanded dock must subtract the native top inset there too; the Android
// browser keeps its webview below the status bar and needs no adjustment.
const isNativeAndroidApp = () =>
    typeof window !== 'undefined' && Boolean(window.KNOVIGATOR_IS_MOBILE) && isAndroid

const shouldStartTrackAtBeginning = (source, options = {}) => {
    if (options.startAtBeginning === true) return true
    if (options.startAtBeginning === false) return false
    return START_AT_BEGINNING_SOURCES.has(source)
}

const readPlaybackModes = () => {
    if (typeof window === 'undefined') return { loop: false, shuffle: false, radio: false }

    try {
        const parsed = JSON.parse(window.localStorage.getItem(PLAYBACK_MODE_STORAGE_KEY) || 'null')
        return {
            loop: parsed?.loop === true,
            shuffle: parsed?.shuffle === true,
            // Radio is an active listening session, not a saved preference.
            // Restoring it lets a cable connection start audio without a tap.
            radio: false,
        }
    } catch {
        return { loop: false, shuffle: false, radio: false }
    }
}

const writePlaybackModes = modes => {
    if (typeof window === 'undefined') return

    try {
        window.localStorage.setItem(
            PLAYBACK_MODE_STORAGE_KEY,
            JSON.stringify({
                loop: modes.loop === true,
                shuffle: modes.shuffle === true,
            }),
        )
    } catch {}
}

const uniqueKeys = keys => Array.from(new Set(keys.filter(Boolean)))

const formatDockTime = seconds => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

    const totalSeconds = Math.floor(seconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainingSeconds = totalSeconds % 60

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(
            2,
            '0',
        )}`
    }
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

const buildDockProgressStyle = progressPercent =>
    /** @type {React.CSSProperties & { '--dock-progress': string }} */ ({
        '--dock-progress': `${progressPercent}%`,
    })

const clampDockVolume = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.max(0, Math.min(1, numericValue)) : null
}

const isVideoLikeTrack = track => track?.kind === 'video' || track?.kind === 'youtube'
const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const Equalizer = ({ className = undefined }) => (
    <span className={cn(styles.equalizer, className)} aria-hidden="true">
        <span />
        <span />
        <span />
    </span>
)

/**
 * @param {{ track?: any, posterSrc?: string | null }} props
 */
function TrackThumbView({ track, posterSrc = null }) {
    const displayPosterSrc = posterSrc || track?.posterSrc || null

    return (
        <span
            className={cn(styles.thumb, displayPosterSrc && styles.thumbWithPoster)}
            style={displayPosterSrc ? { backgroundImage: `url(${displayPosterSrc})` } : undefined}
            aria-hidden="true"
        >
            {!displayPosterSrc &&
                (isVideoLikeTrack(track) ? (
                    <Film className={styles.thumbIcon} />
                ) : (
                    <Music2 className={styles.thumbIcon} />
                ))}
        </span>
    )
}

const TrackThumb = React.memo(TrackThumbView)

/**
 * @param {{
 *   items: any[],
 *   tab: 'playlist' | 'history',
 *   nowPlayingKey?: string | null,
 *   isPlaying: boolean,
 *   trackAnswerTitles: Record<string, string | null | undefined>,
 *   trackAnswerArtwork: Record<string, string | null | undefined>,
 *   onTogglePlayback: (source?: string) => void,
 *   onPlayTrack: (track: any, source?: string, options?: { startAtBeginning?: boolean }) => void,
 *   onHistoryChanged: () => void,
 *   hasMore: boolean,
 *   isLoading: boolean,
 *   onLoadMore: (tab: 'playlist' | 'history') => void,
 * }} props
 */
function DockTrackListView({
    items,
    tab,
    nowPlayingKey,
    isPlaying,
    trackAnswerTitles,
    trackAnswerArtwork,
    onTogglePlayback,
    onPlayTrack,
    onHistoryChanged,
    hasMore,
    isLoading,
    onLoadMore,
}) {
    const listRef = useRef(null)
    const loadMoreIfNearEnd = useCallback(() => {
        const list = listRef.current
        if (!list || !hasMore || isLoading) return
        if (list.scrollHeight - list.scrollTop - list.clientHeight > 160) return

        onLoadMore(tab)
    }, [hasMore, isLoading, onLoadMore, tab])

    useEffect(() => {
        loadMoreIfNearEnd()
    }, [items.length, loadMoreIfNearEnd])

    return (
        <div ref={listRef} className={styles.historyList} onScroll={loadMoreIfNearEnd}>
            {items.length === 0 ? (
                <div className={styles.listEmpty}>
                    {tab === 'playlist'
                        ? 'Nothing queued yet — tap + on the player, in History, or in any post’s media menu.'
                        : 'Nothing played yet.'}
                </div>
            ) : (
                items.map(item => {
                    const isActive = item.mediaKey === nowPlayingKey
                    const stored = getMediaPlaybackState(item.mediaKey)
                    const resumeTime = Number.isFinite(stored?.currentTime) ? stored.currentTime : 0
                    const showResumeTime = tab !== 'playlist' && resumeTime > 0.05
                    const inPlaylist = isMediaTrackInPlaylist(item.mediaKey)
                    const answerId = item?.answerId ? String(item.answerId) : null
                    const itemTitle =
                        (answerId ? trackAnswerTitles[answerId] : null) ||
                        getMediaDockTrackDisplayTitle(item, {
                            getAnswerById: answerId => getCachedAnswer(answerId),
                        }) ||
                        'Untitled'
                    const itemArtwork = getMediaDockTrackDisplayArtwork(item, {
                        answerArtwork: answerId ? trackAnswerArtwork[answerId] : null,
                        getAnswerById: answerId => getCachedAnswer(answerId),
                    })

                    return (
                        <div
                            key={item.mediaKey}
                            className={cn(styles.historyItem, isActive && styles.historyItemActive)}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                                isActive ? onTogglePlayback(tab) : onPlayTrack(item, tab)
                            }
                            onKeyDown={event => {
                                if (event.key !== 'Enter' && event.key !== ' ') return
                                event.preventDefault()
                                if (isActive) onTogglePlayback(tab)
                                else onPlayTrack(item, tab)
                            }}
                        >
                            <TrackThumb track={item} posterSrc={itemArtwork} />
                            <span className={styles.historyItemMeta}>
                                <span className={styles.historyItemTitle} title={itemTitle}>
                                    {itemTitle}
                                </span>
                                <span className={styles.historyItemStatus}>
                                    {isActive && isPlaying ? (
                                        <>
                                            <Equalizer /> Playing
                                        </>
                                    ) : showResumeTime ? (
                                        `Resume ${formatDockTime(resumeTime)}`
                                    ) : isVideoLikeTrack(item) ? (
                                        'Video'
                                    ) : (
                                        'Audio'
                                    )}
                                </span>
                            </span>
                            {tab === 'history' && (
                                <button
                                    type="button"
                                    className={cn(
                                        styles.historyItemAction,
                                        inPlaylist && styles.historyItemActionDone,
                                    )}
                                    onClick={event => {
                                        event.stopPropagation()
                                        if (inPlaylist) {
                                            removeMediaTrackFromPlaylist(item.mediaKey)
                                        } else {
                                            addTrackToPlaylist(item.mediaKey, 'history_tab', {
                                                notify: false,
                                            })
                                        }
                                    }}
                                    aria-label={
                                        inPlaylist ? 'Remove from playlist' : 'Add to playlist'
                                    }
                                    title={inPlaylist ? 'In playlist' : 'Add to playlist'}
                                >
                                    {inPlaylist ? (
                                        <Check aria-hidden="true" />
                                    ) : (
                                        <Plus aria-hidden="true" />
                                    )}
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.historyItemRemove}
                                onClick={event => {
                                    event.stopPropagation()
                                    if (tab === 'playlist') {
                                        removeMediaTrackFromPlaylist(item.mediaKey)
                                    } else {
                                        removeMediaTrackFromHistory(item.mediaKey)
                                        onHistoryChanged()
                                    }
                                }}
                                aria-label={
                                    tab === 'playlist'
                                        ? 'Remove from playlist'
                                        : 'Remove from history'
                                }
                            >
                                <X aria-hidden="true" />
                            </button>
                        </div>
                    )
                })
            )}
            {items.length > 0 && isLoading && (
                <div className={styles.listLoading} role="status">
                    Loading more…
                </div>
            )}
        </div>
    )
}

const DockTrackList = React.memo(DockTrackListView)

const RADIO_ODDS_SLICES = 6

// The next draw as one bar: the leading backed songs as their own slices,
// the rest of the backed share as one, and the discovery share. The block
// hash lands somewhere on it, so a wider slice is literally a better chance.
function RadioDrawOdds({ tracks, odds }) {
    const backed = tracks.filter(track => track.chanceSupport > 0)
    // Whole-draw totals from the server; the listed songs alone undercount
    // when the pool is longer than the page.
    const backedShare = odds?.support ?? backed.reduce((sum, track) => sum + track.chanceSupport, 0)
    const discoveryShare =
        odds?.discovery ?? tracks.reduce((sum, track) => sum + track.chanceDiscovery, 0)
    const discoveryCount =
        odds?.discoveryCount ?? tracks.filter(track => track.chanceDiscovery > 0).length
    if (backedShare + discoveryShare <= 0) return null

    const leaders = backed.slice(0, RADIO_ODDS_SLICES)
    const othersShare = backedShare - leaders.reduce((sum, track) => sum + track.chanceSupport, 0)

    return (
        <div className={styles.radioOdds}>
            <div className={styles.radioOddsBar} role="img" aria-label="Chances in the next draw">
                {leaders.map((track, index) => (
                    <span
                        key={track.mediaKey}
                        className={styles.radioOddsSlice}
                        style={{
                            flexGrow: track.chanceSupport,
                            opacity: 1 - (index * 0.5) / RADIO_ODDS_SLICES,
                        }}
                        title={`${track.title || 'Untitled'} · ${formatRadioChance(
                            track.chanceSupport,
                        )}`}
                    />
                ))}
                {othersShare > 0.0001 && (
                    <span
                        className={cn(styles.radioOddsSlice, styles.radioOddsSliceOthers)}
                        style={{ flexGrow: othersShare }}
                        title={`Other backed songs · ${formatRadioChance(othersShare)}`}
                    />
                )}
                {discoveryShare > 0 && (
                    <span
                        className={cn(styles.radioOddsSlice, styles.radioOddsSliceDiscovery)}
                        style={{ flexGrow: discoveryShare }}
                        title={`Discovery · ${formatRadioChance(discoveryShare)}`}
                    />
                )}
            </div>
            <div className={styles.radioOddsLegend}>
                <span>
                    <b>Backed {formatRadioChance(backedShare)}</b> · playlists × √sats
                </span>
                {discoveryShare > 0 && (
                    <span className={styles.radioOddsLegendDiscovery}>
                        <b>Discovery {formatRadioChance(discoveryShare)}</b> · {discoveryCount}{' '}
                        unheard this week
                    </span>
                )}
            </div>
            <div className={styles.radioOddsExplainer}>
                Each draw, the next BSV block picks a point on this bar. Upvalue a song to widen its
                slice.
            </div>
        </div>
    )
}

/**
 * Treechat Radio tab: the station's rotation, ranked by chance in the next
 * draw. Display only — the broadcast is live, so songs cannot be picked individually.
 *
 * @param {{
 *   pool: { status: string, tracks: any[], odds?: { support: number, discovery: number, discoveryCount: number } | null },
 *   nowPlayingKey?: string | null,
 *   isPlaying: boolean,
 *   trackAnswerTitles: Record<string, string | null | undefined>,
 *   trackAnswerArtwork: Record<string, string | null | undefined>,
 *   formatSats: (sats: number) => string,
 * }} props
 */
function RadioTrackListView({
    pool,
    nowPlayingKey,
    isPlaying,
    trackAnswerTitles,
    trackAnswerArtwork,
    formatSats,
}) {
    if (pool.status === 'idle' || pool.status === 'loading') {
        return (
            <div className={styles.historyList}>
                <div className={styles.listEmpty}>Tuning in…</div>
            </div>
        )
    }

    if (pool.tracks.length === 0) {
        return (
            <div className={styles.historyList}>
                <div className={styles.listEmpty}>
                    Nothing on the air yet — upvalue some songs to build the rotation.
                </div>
            </div>
        )
    }

    const maxChance = Math.max(0, ...pool.tracks.map(track => track.chance || 0))

    // Display-only: this is a live broadcast, so individual songs cannot be
    // picked — everyone hears the station. Ordered by chance in the next draw.
    return (
        <>
            <RadioDrawOdds tracks={pool.tracks} odds={pool.odds} />
            <div className={styles.historyList}>
                {pool.tracks.map(item => {
                    const answerId = item?.answerId ? String(item.answerId) : null
                    const itemTitle =
                        (answerId ? trackAnswerTitles[answerId] : null) ||
                        getMediaDockTrackDisplayTitle(item, {
                            getAnswerById: answerId => getCachedAnswer(answerId),
                        }) ||
                        'Untitled'
                    const itemArtwork = getMediaDockTrackDisplayArtwork(item, {
                        answerArtwork: answerId ? trackAnswerArtwork[answerId] : null,
                        getAnswerById: answerId => getCachedAnswer(answerId),
                    })
                    const isActive = item.mediaKey === nowPlayingKey

                    return (
                        <RadioChanceRow
                            key={item.mediaKey}
                            title={itemTitle}
                            artworkUrl={itemArtwork}
                            isVideo={isVideoLikeTrack(item)}
                            chance={item.chance}
                            chanceSupport={item.chanceSupport}
                            chanceDiscovery={item.chanceDiscovery}
                            playlistCount={item.playlistCount}
                            upvalueSats={item.upvalueSats}
                            maxChance={maxChance}
                            formatSats={formatSats}
                            isActive={isActive}
                            activeLabel={isActive && isPlaying ? 'On air' : null}
                        />
                    )
                })}
            </div>
        </>
    )
}

const RadioTrackList = React.memo(RadioTrackListView)

/**
 * Floating mini media player. Stays hidden while the inline player is on
 * screen; once the playing post scrolls away, navigates away, or unmounts,
 * the dock appears beside the New button on mobile (above it on desktop) and
 * continues playback from the same position with its own element. Collapsed
 * video shows YouTube-style tap controls; expanded mode adds seek, transport,
 * and Playlist/History tabs.
 */
export default function MediaDock() {
    incrementMediaDockQaCounter('dock.rootRenders')
    const [snapshot, setSnapshot] = useState(getMediaDockSnapshot)
    const [isExpanded, setIsExpanded] = useState(false)
    // Cinema: the expanded dock's stage fills the app window (never the
    // browser's fullscreen). Implies isExpanded; every collapse ends it.
    const [isCinema, setIsCinema] = useState(false)
    const isCinemaRef = useRef(false)
    isCinemaRef.current = isCinema
    const cinemaFeatureEnabled = mediaDockCinemaEnabled()
    const cinemaFeatureEnabledRef = useRef(cinemaFeatureEnabled)
    cinemaFeatureEnabledRef.current = cinemaFeatureEnabled
    // Where the picture came from, so leaving cinema can put it back.
    // presentation: 'expanded' | 'hosted' | 'corner' | 'none'
    const cinemaReturnRef = useRef(null) // { mediaKey, presentation, hostedPlacement, originEl }
    const cinemaFocusReturnRef = useRef(null) // { element, selector }
    // Cinema chrome fades while the video plays and nothing has moved for a
    // while; pointer intent over the controls holds it.
    const [cinemaChromeVisible, setCinemaChromeVisible] = useState(true)
    const cinemaChromeTimerRef = useRef(null)
    // What is holding the chrome up: a pointer resting on a control, keyboard
    // focus on one, an open settings menu. Each lets go on its own; the fade
    // waits for all of them.
    const cinemaChromePinsRef = useRef(new Set())
    // What the cinema transport's gear shows for the dock's runtime, and
    // whether the dock root is in the browser's fullscreen.
    const [stageRuntimeControls, setStageRuntimeControls] = useState(EMPTY_STAGE_RUNTIME_CONTROLS)
    const [isCinemaFullscreen, setIsCinemaFullscreen] = useState(false)
    const [isClosed, setIsClosed] = useState(false)
    // Sticky: once the dock has appeared it stays until dismissed, even when
    // the original media scrolls back on screen.
    const [isLaunched, setIsLaunched] = useState(false)
    const [takeover, setTakeover] = useState(null) // { mediaKey, resumeToken }
    const [isLanding, setIsLanding] = useState(false)
    const [isDraggingSeek, setIsDraggingSeek] = useState(false)
    const [volume, setVolume] = useState(1)
    const [overlayVisible, setOverlayVisible] = useState(false)
    const [usesHoverVideoOverlay, setUsesHoverVideoOverlay] = useState(canUseHoverVideoControls)
    const [isVideoSurfaceActive, setIsVideoSurfaceActive] = useState(false)
    const [activeTab, setActiveTab] = useState('history') // 'playlist' | 'history' | 'radio'
    const [playbackModes, setPlaybackModes] = useState(readPlaybackModes)
    const loopEnabled = playbackModes.loop
    const shuffleEnabled = playbackModes.shuffle
    const radioFeatureEnabled = treechatRadioEnabled()
    const radioEnabled = radioFeatureEnabled && playbackModes.radio
    const radioFeatureRef = useRef(radioFeatureEnabled)
    radioFeatureRef.current = radioFeatureEnabled
    // Treechat Radio: the station's rotation (display) and the live broadcast
    // ({serverNow, now, next}) the dock is synced to.
    const [radioPool, setRadioPool] = useState({ status: 'idle', tracks: [], odds: null })
    const radioPoolRef = useRef([])
    radioPoolRef.current = radioPool.tracks
    const [radioLive, setRadioLive] = useState(null)
    const radioLiveRef = useRef(null)
    // Dynamic playlist: whether this listener's upvalues broadcast
    // "@user is playing …" and back the song on the station. Public by default.
    const [broadcastPlays, setBroadcastPlays] = useState(nowPlayingBroadcastEnabled)
    const radioWaitingRef = useRef(false)
    const radioSyncGenerationRef = useRef(0)
    // Consecutive dead-source holds; caps the retry chain when the pool is
    // unplayable so the radio does not loop forever.
    const radioErrorSkipsRef = useRef(0)
    // Frozen ordering for prev/next stepping: claiming a track moves it to the
    // front of history, so navigating against live history can only ever go
    // backwards. The queue is captured when stepping starts and cleared on
    // any organic (non prev/next) play.
    const [navQueue, setNavQueue] = useState(null) // array of mediaKeys | null
    // Measured mobile placement keeps the dock beside New; desktop only uses
    // the bottom offset when the dock would overlap that button.
    const [dockBottom, setDockBottom] = useState(null)
    const [dockRight, setDockRight] = useState(null)
    const [mobileDockPosition, setMobileDockPosition] = useState(null)
    const [isDraggingDock, setIsDraggingDock] = useState(false)
    const [dockAttention, setDockAttention] = useState(false)
    const [youtubePreloadSyncToken, setYoutubePreloadSyncToken] = useState(0)
    const [youtubePreloadReadyMediaKey, setYoutubePreloadReadyMediaKey] = useState(null)
    const [isDocumentVisible, setIsDocumentVisible] = useState(
        () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
    )
    // Hosted playback: the dock's element is the ONLY runtime for the track and
    // sits exactly over the inline origin frame while that frame is on screen.
    // When the frame leaves (scroll, swipe, panel change, unmount) the same
    // element glides to the corner — no second player, no handoff.
    const hostedPlaybackEnabled = hostedMediaPlaybackEnabled()
    const hostedPlaybackEnabledRef = useRef(hostedPlaybackEnabled)
    hostedPlaybackEnabledRef.current = hostedPlaybackEnabled
    const [hostedPlacement, setHostedPlacement] = useState(null) // { mediaKey, originEl }
    const hostedPlacementRef = useRef(null)
    const pendingHostedPlayRef = useRef(null)
    hostedPlacementRef.current = hostedPlacement
    // Latest tracked origin geometry, written per frame outside React.
    const hostedGeometryRef = useRef(null) // { rect, clip, cornerRadii }
    // Set when a playing hosted runtime leaves its origin: the next presented
    // corner render morphs from that rect.
    const pendingCornerMorphRef = useRef(null) // { mediaKey, rect }
    const dockBottomRef = useRef(null)
    const dockRightRef = useRef(null)
    const mobileDockPositionRef = useRef(null)
    const dockDragRef = useRef(null)
    const lastDockBottomMeasureAtRef = useRef(0)
    const dockOwnerIdRef = useRef(null)
    if (!dockOwnerIdRef.current) {
        dockOwnerIdRef.current = createMediaPlaybackOwnerId('media-dock')
    }

    const rootRef = useRef(null)
    const cinemaExitButtonRef = useRef(null)
    const mediaSurfaceRef = useRef(null)
    const mediaSurfaceHomeRef = useRef(null)
    // Media key the dock itself just paused, so the resulting release event is
    // not mistaken for a system pause the dock should undo.
    const dockPauseIntentRef = useRef(null)
    // Last rect of the origin while it was still on screen, so the entrance
    // morph launches from where the user last saw the video — by adoption time
    // the element has usually already been swiped or scrolled away.
    const originRectRef = useRef(null) // { mediaKey, rect }
    // Tracks onscreen→offscreen transitions of the playing track's origin,
    // which is the moment a playing video hands off to the dock.
    const originOnScreenRef = useRef({ mediaKey: null, onScreen: false })
    // Deduplicates media_play answer-action records per now-playing change.
    const lastPlayRecordedKeyRef = useRef(null)
    const overlayTimerRef = useRef(null)
    const dockAttentionTimerRef = useRef(null)
    const pendingPlaylistFocusRef = useRef(null)
    const volumeRef = useRef(1)
    volumeRef.current = volume
    const playbackModesRef = useRef(playbackModes)
    const playlistRef = useRef([])
    const playTrackRef = useRef(null)
    const syncRadioLiveRef = useRef(null)
    const stopRadioRef = useRef(null)
    const startRadioRef = useRef(null)
    const openRadioFromRequestRef = useRef(null)
    const openHistoryFromRequestRef = useRef(null)
    const openCinemaFromRequestRef = useRef(null)
    const playInDockFromRequestRef = useRef(null)
    const shufflePlayedKeysRef = useRef([])
    // True while a claim caused by the dock's own prev/next is in flight.
    const navigatingRef = useRef(false)

    playbackModesRef.current = playbackModes
    playlistRef.current = snapshot.playlist || []

    const getVisibleOrigin = useCallback(mediaKey => getVisibleMediaTrackOrigin(mediaKey), [])

    const captureOriginRect = useCallback((mediaKey, rect = null) => {
        if (rect) {
            originRectRef.current = { mediaKey, rect }
        }
    }, [])

    // Attach the dock's runtime to an inline origin frame. Only a frame the
    // user can currently see qualifies; otherwise the runtime plays in the
    // corner as before. A 'visual' placement parks the dock over the frame; a
    // 'silent' one (audio has no picture) keeps the dock hidden and only
    // watches for the frame leaving.
    const enterHostedPlacement = useCallback((mediaKey, originEl, kind = 'visual') => {
        const placement = getMediaTrackOriginPlacement(mediaKey, { originEl })
        if (!placement?.visible || !placement.rect) return false
        const previous = hostedPlacementRef.current
        const samePlacement = Boolean(
            previous &&
                previous.mediaKey === mediaKey &&
                previous.originEl === placement.element &&
                previous.kind === kind &&
                (previous.positionMode !== 'flow' ||
                    mediaSurfaceRef.current?.parentNode === placement.element),
        )

        // The frame's on-screen curve (often its card's clip, not its own
        // radius), which the runtime restates in every mode.
        const cornerRadii =
            kind !== 'silent' ? resolveHostedCornerRadii(placement.element, placement.rect) : null
        hostedGeometryRef.current = {
            rect: placement.rect,
            clip: computeHostedClipInset(placement.rect, placement.bounds),
            cornerRadii,
        }
        // Preserve the running tracker for an exact re-tap. Restarting it would
        // zero its residual accumulator while the old transform is still on
        // the DOM, causing a corrective oscillation on transformed panels.
        if (!samePlacement) {
            const rootEl = rootRef.current
            const mediaSurfaceEl = mediaSurfaceRef.current
            let positionMode = 'tracked'

            if (previous?.positionMode === 'anchored') {
                replaceHostedAnchorElement(previous.originEl, null)
            }

            // Prefer a real DOM relationship. moveBefore() keeps an iframe's
            // browsing context alive, so the one playing surface can become a
            // child of the post and inherit scroll/panel motion with no visual
            // tracking writes at all.
            if (
                kind !== 'silent' &&
                HOSTED_STATE_PRESERVING_MOVE &&
                moveHostedPresentation(mediaSurfaceEl, placement.element)
            ) {
                positionMode = 'flow'
                resetHostedAnchoredStyle(rootEl)
                mediaSurfaceEl.style.setProperty(
                    '--hosted-frame-clip',
                    buildHostedFlowClipPath(cornerRadii),
                )
            } else {
                if (previous?.positionMode === 'flow') {
                    restoreHostedPresentation(mediaSurfaceEl, mediaSurfaceHomeRef.current)
                    mediaSurfaceEl?.style.removeProperty('--hosted-frame-clip')
                }
                // CSS anchoring remains the no-reload fallback. A direct switch
                // between posts must leave only the requested frame named.
                if (kind !== 'silent' && HOSTED_ANCHOR_POSITIONING) {
                    replaceHostedAnchorElement(null, placement.element)
                    resetHostedAnchoredStyle(rootEl)
                    positionMode = 'anchored'
                }
            }

            const next = { mediaKey, originEl: placement.element, kind, positionMode }
            hostedPlacementRef.current = next
            setHostedPlacement(next)
            setMediaDockHostedPlacement(next)
        }
        setIsExpanded(false)
        setIsCinema(false)
        setIsClosed(false)
        return true
    }, [])

    // A playing runtime that left its post returns over the same media when
    // the place the user navigated to shows it (the post's thread, or a panel
    // swiped to), instead of staying docked in the corner. The post re-parks it
    // through its own "Play here", which knows which of its frames to use.
    const hostedReturnWatchRef = useRef(null) // { timer }

    const cancelHostedReturnWatch = useCallback(() => {
        if (hostedReturnWatchRef.current?.timer) {
            window.clearTimeout(hostedReturnWatchRef.current.timer)
        }
        hostedReturnWatchRef.current = null
    }, [])

    const watchForHostedReturn = useCallback(
        (mediaKey, leftOriginEl) => {
            cancelHostedReturnWatch()
            const startedAt = Date.now()
            // Each post is asked once: one that declines is not asked again.
            const announced = new Set()

            const check = () => {
                hostedReturnWatchRef.current = null
                if (hostedPlacementRef.current || isCinemaRef.current) return
                const latest = latestRef.current
                if (latest.isClosed || latest.isExpanded || latest.takeoverKey !== mediaKey) return
                const current = getMediaDockSnapshot()
                if (current.nowPlayingMediaKey !== mediaKey || !current.isPlaying) return

                const visibleOrigin = getVisibleMediaTrackOrigin(mediaKey, {
                    exclude: leftOriginEl,
                })
                if (visibleOrigin && !announced.has(visibleOrigin.element)) {
                    announced.add(visibleOrigin.element)
                    announceMediaDockHostedReturn(mediaKey, visibleOrigin.element)
                    if (hostedPlacementRef.current) {
                        pendingCornerMorphRef.current = null
                        return
                    }
                }
                if (Date.now() - startedAt < HOSTED_RETURN_WATCH_WINDOW_MS) {
                    hostedReturnWatchRef.current = {
                        timer: window.setTimeout(check, HOSTED_RETURN_WATCH_TICK_MS),
                    }
                }
            }

            check()
        },
        [cancelHostedReturnWatch],
    )

    useEffect(() => cancelHostedReturnWatch, [cancelHostedReturnWatch])

    // The origin left the screen (or unmounted). A playing runtime glides to
    // the corner from where the user last saw it; a paused one is released —
    // a user pause followed by navigation must not launch the dock.
    const leaveHostedPlacement = useCallback(
        (reason, options = {}) => {
            const current = hostedPlacementRef.current
            if (!current) return
            const geometry = hostedGeometryRef.current
            exitHostedSurfaceFullscreen([rootRef.current, mediaSurfaceRef.current])
            if (current.positionMode === 'flow') {
                restoreHostedPresentation(mediaSurfaceRef.current, mediaSurfaceHomeRef.current)
                mediaSurfaceRef.current?.style.removeProperty('--hosted-frame-clip')
            } else if (current.positionMode === 'anchored') {
                replaceHostedAnchorElement(current.originEl, null)
            }
            hostedPlacementRef.current = null
            hostedGeometryRef.current = null
            setHostedPlacement(null)
            setMediaDockHostedPlacement(null)
            logMediaEvent('MEDIA_DOCK_HOSTED_DETACH', { media_key: current.mediaKey, reason })

            if (options.keepRuntime) return
            const snapshot = getMediaDockSnapshot()
            if (
                !shouldKeepHostedRuntime(current.mediaKey, snapshot, pendingHostedPlayRef.current)
            ) {
                setTakeover(null)
                setIsLaunched(false)
                return
            }
            if (geometry?.rect) {
                captureOriginRect(current.mediaKey, geometry.rect)
            }
            pendingCornerMorphRef.current = {
                mediaKey: current.mediaKey,
                rect: geometry?.rect || null,
            }
            // A hosted station song from the Music header's tuner: the dock
            // already plays it, so its leaving is the radio handoff (Radio
            // tab, following the station), as for an inline element.
            if (
                radioFeatureRef.current &&
                !playbackModesRef.current.radio &&
                isInlineRadioKey(current.mediaKey)
            ) {
                setActiveTab('radio')
                startRadioRef.current?.('inline_radio_handoff')
            }
            if (HOSTED_RETURN_REASONS.has(reason)) {
                watchForHostedReturn(current.mediaKey, current.originEl)
            }
        },
        [captureOriginRect, watchForHostedReturn],
    )

    // Expanded dock chrome belongs to the dock, never to an inline host.
    // Detach the presentation while retaining the current playback runtime.
    const expandDock = useCallback(() => {
        leaveHostedPlacement('expanded', { keepRuntime: true })
        setIsExpanded(true)
    }, [leaveHostedPlacement])

    useEffect(() => {
        if (typeof document === 'undefined') return undefined
        const handleVisibilityChange = () => {
            setIsDocumentVisible(document.visibilityState !== 'hidden')
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [])

    useLayoutEffect(() => {
        if (typeof document === 'undefined' || typeof window === 'undefined') return undefined

        const documentRoot = document.documentElement
        const syncExpandedMobileState = () => {
            documentRoot.toggleAttribute(
                MOBILE_DOCK_EXPANDED_ATTRIBUTE,
                isExpanded && window.innerWidth <= 900,
            )
        }

        syncExpandedMobileState()
        window.addEventListener('resize', syncExpandedMobileState)
        return () => {
            window.removeEventListener('resize', syncExpandedMobileState)
            documentRoot.removeAttribute(MOBILE_DOCK_EXPANDED_ATTRIBUTE)
        }
    }, [isExpanded])

    // On mobile, align the dock with the New button and place it immediately
    // to the button's left. Desktop keeps the existing stacked placement.
    const measureDockPlacement = useCallback(() => {
        incrementMediaDockQaCounter('layout.dockBottomMeasurements')
        if (typeof document === 'undefined') return null
        const isMobileViewport = window.innerWidth <= 900
        // fixed_header phones slide the navbar and New away on scroll
        // (html[data-mobile-chrome="hidden"]): the dock then drops to the
        // bottom edge, and its controls are measured where they rest.
        const chromeHidden = isMobileViewport && isMobileChromeHidden()
        const edgeBottom = MOBILE_DOCK_MARGIN_PX + readNativeSafeAreaBottom()
        const button = document.querySelector('[data-new-quest-button]')
        if (button) {
            incrementMediaDockQaCounter('layout.boundingClientRectReads')
            const measured = button.getBoundingClientRect()
            const slideY = bottomControlsSlideY(button)
            const rect = slideY
                ? {
                      left: measured.left,
                      width: measured.width,
                      height: measured.height,
                      top: measured.top - slideY,
                      bottom: measured.bottom - slideY,
                      right: measured.right,
                  }
                : measured
            if (rect.width && rect.height) {
                if (isMobileViewport) {
                    const anchor = getMobileMediaDockAnchor({
                        viewportWidth: window.innerWidth,
                        viewportHeight: window.innerHeight,
                        newButtonRect: rect,
                        gap: MOBILE_DOCK_CONTROL_GAP_PX,
                    })
                    return anchor && chromeHidden ? { ...anchor, bottom: edgeBottom } : anchor
                }

                if (rootRef.current) {
                    incrementMediaDockQaCounter('layout.boundingClientRectReads')
                }
                const dockWidth = rootRef.current?.getBoundingClientRect()?.width || 332
                const dockLeft = window.innerWidth - 16 - dockWidth
                if (rect.right > dockLeft) {
                    return {
                        bottom: Math.round(
                            window.innerHeight - rect.top + MOBILE_DOCK_CONTROL_GAP_PX,
                        ),
                        right: null,
                    }
                }
            }
        }

        if (!isMobileViewport) return null

        const bottomNavigation = document.querySelector('[data-bottom-navigation]')
        if (!bottomNavigation) return null
        incrementMediaDockQaCounter('layout.boundingClientRectReads')
        const navigationRect = bottomNavigation.getBoundingClientRect()
        if (!navigationRect.width || !navigationRect.height) return null
        if (chromeHidden) return { bottom: edgeBottom, right: null }

        // At rest: the controls may be sliding on scroll, and the navbar itself
        // may be rising back from the edge after the keyboard.
        const navigationTop = resolveNavigationTop({
            rectTop: navigationRect.top - bottomControlsSlideY(bottomNavigation),
            transform: window.getComputedStyle(bottomNavigation).transform,
        })
        return {
            bottom: Math.round(window.innerHeight - navigationTop + MOBILE_DOCK_CONTROL_GAP_PX),
            right: null,
        }
    }, [])

    const syncDockPlacement = useCallback(
        ({ force = false } = {}) => {
            const now = Date.now()
            if (
                !force &&
                lastDockBottomMeasureAtRef.current &&
                now - lastDockBottomMeasureAtRef.current < DOCK_BOTTOM_MEASURE_INTERVAL_MS
            ) {
                return {
                    bottom: dockBottomRef.current,
                    right: dockRightRef.current,
                }
            }

            lastDockBottomMeasureAtRef.current = now
            const nextDockPlacement = measureDockPlacement()
            const nextDockBottom = nextDockPlacement?.bottom ?? null
            const nextDockRight = nextDockPlacement?.right ?? null
            dockBottomRef.current = nextDockBottom
            dockRightRef.current = nextDockRight
            setDockBottom(previousDockBottom =>
                previousDockBottom === nextDockBottom ? previousDockBottom : nextDockBottom,
            )
            setDockRight(previousDockRight =>
                previousDockRight === nextDockRight ? previousDockRight : nextDockRight,
            )
            return nextDockPlacement
        },
        [measureDockPlacement],
    )

    // fixed_header phones: follow the navbar as it slides away and back.
    useEffect(() => {
        if (typeof MutationObserver === 'undefined') return undefined
        const observer = new MutationObserver(() => syncDockPlacement({ force: true }))
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-mobile-chrome'],
        })
        return () => observer.disconnect()
    }, [syncDockPlacement])

    const constrainDockPosition = useCallback((requestedLeft, requestedTop) => {
        const dockRect = rootRef.current?.getBoundingClientRect()
        if (!dockRect?.width || !dockRect?.height) return null

        const bottomNavigation = document.querySelector('[data-bottom-navigation]')
        const navigationRect = bottomNavigation?.getBoundingClientRect()
        const bottomBoundary =
            navigationRect?.width && navigationRect?.height && !isMobileChromeHidden()
                ? navigationRect.top
                : window.innerHeight
        const nativeTopSafeArea = Number.parseFloat(
            window
                .getComputedStyle(document.documentElement)
                .getPropertyValue('--native-safe-area-top'),
        )

        return constrainMobileMediaDockPosition({
            requestedLeft,
            requestedTop,
            dockWidth: dockRect.width,
            dockHeight: dockRect.height,
            viewportWidth: window.innerWidth,
            bottomBoundary,
            margin: MOBILE_DOCK_MARGIN_PX,
            topBoundary:
                MOBILE_DOCK_MARGIN_PX +
                (Number.isFinite(nativeTopSafeArea) ? nativeTopSafeArea : 0),
        })
    }, [])

    const updateMobileDockPosition = useCallback(nextPosition => {
        if (!nextPosition) return
        mobileDockPositionRef.current = nextPosition
        setMobileDockPosition(nextPosition)
    }, [])

    const handleDockPointerDown = useCallback(
        event => {
            if (typeof window === 'undefined' || window.innerWidth > 900 || isExpanded) return
            if (event.pointerType === 'mouse' && event.button !== 0) return

            const dockRect = rootRef.current?.getBoundingClientRect()
            if (!dockRect) return

            dockDragRef.current = {
                pointerId: event.pointerId,
                offsetX: event.clientX - dockRect.left,
                offsetY: event.clientY - dockRect.top,
            }
            event.currentTarget.setPointerCapture?.(event.pointerId)
            updateMobileDockPosition({ left: dockRect.left, top: dockRect.top })
            setIsDraggingDock(true)
            event.preventDefault()
        },
        [isExpanded, updateMobileDockPosition],
    )

    const handleDockPointerMove = useCallback(
        event => {
            const drag = dockDragRef.current
            if (!drag || drag.pointerId !== event.pointerId) return

            updateMobileDockPosition(
                constrainDockPosition(event.clientX - drag.offsetX, event.clientY - drag.offsetY),
            )
            event.preventDefault()
        },
        [constrainDockPosition, updateMobileDockPosition],
    )

    const handleDockPointerEnd = useCallback(event => {
        if (dockDragRef.current?.pointerId !== event.pointerId) return
        dockDragRef.current = null
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
        }
        setIsDraggingDock(false)
    }, [])

    const handleDockDragKeyDown = useCallback(
        event => {
            if (typeof window === 'undefined' || window.innerWidth > 900 || isExpanded) return
            const direction = {
                ArrowLeft: [-1, 0],
                ArrowRight: [1, 0],
                ArrowUp: [0, -1],
                ArrowDown: [0, 1],
            }[event.key]
            if (!direction) return

            const dockRect = rootRef.current?.getBoundingClientRect()
            if (!dockRect) return
            const step = event.shiftKey ? 40 : 10
            updateMobileDockPosition(
                constrainDockPosition(
                    dockRect.left + direction[0] * step,
                    dockRect.top + direction[1] * step,
                ),
            )
            event.preventDefault()
            event.stopPropagation()
        },
        [constrainDockPosition, isExpanded, updateMobileDockPosition],
    )

    // Latest values for use inside the long-lived dock subscription.
    // Whether the dock is on screen at all this render (corner, expanded, or
    // parked over a post); read by handlers that need the pre-action state.
    const isPresentedRef = useRef(false)
    const latestRef = useRef({
        isClosed: false,
        isExpanded: false,
        isLaunched: false,
        takeoverKey: null,
        takeoverResumeToken: 0,
    })
    latestRef.current = {
        isClosed,
        isExpanded,
        isLaunched,
        takeoverKey: takeover?.mediaKey || null,
        takeoverResumeToken: takeover?.resumeToken || 0,
    }
    // True while a gated takeover is holding the dock hidden; assigned below
    // once the reveal gate is derived, read by the playback poll.
    const dockHiddenPreparingRef = useRef(false)
    // Consecutive poll ticks the origin has been back on screen during a
    // hidden takeover.
    const originReturnRef = useRef(null) // { mediaKey, ticks }

    const triggerDockAttention = useCallback(() => {
        if (dockAttentionTimerRef.current) {
            window.clearTimeout(dockAttentionTimerRef.current)
        }

        setDockAttention(false)
        dockAttentionTimerRef.current = window.setTimeout(() => {
            setDockAttention(true)
            dockAttentionTimerRef.current = window.setTimeout(() => {
                setDockAttention(false)
                dockAttentionTimerRef.current = null
            }, 520)
        }, 0)
    }, [])

    useEffect(() => {
        return () => {
            if (dockAttentionTimerRef.current) {
                window.clearTimeout(dockAttentionTimerRef.current)
            }
        }
    }, [])

    useEffect(() => {
        writePlaybackModes(playbackModes)
    }, [playbackModes])

    useEffect(() => {
        const playlistKeys = new Set((snapshot.playlist || []).map(item => item?.mediaKey))
        shufflePlayedKeysRef.current = shufflePlayedKeysRef.current.filter(key =>
            playlistKeys.has(key),
        )
    }, [snapshot.playlist])

    const seedShufflePlayback = useCallback(mediaKey => {
        const playlistKeys = new Set((playlistRef.current || []).map(item => item?.mediaKey))
        shufflePlayedKeysRef.current = mediaKey && playlistKeys.has(mediaKey) ? [mediaKey] : []
    }, [])

    const selectNextPlaylistTrack = useCallback((currentMediaKey, options = {}) => {
        const playlistItems = (playlistRef.current || []).filter(item => item?.mediaKey)
        const currentIndex = playlistItems.findIndex(item => item.mediaKey === currentMediaKey)
        if (options.requireCurrent && currentIndex < 0) return null

        const { loop, shuffle } = playbackModesRef.current
        const result = getNextPlaylistTrack(playlistItems, currentMediaKey, {
            loop,
            shuffle,
            playedKeys: shufflePlayedKeysRef.current,
            random: shuffle ? Math.random : null,
        })
        if (shuffle) {
            shufflePlayedKeysRef.current = result.playedKeys
        }
        return result.track
    }, [])

    // Restore the playlist + history (and resume positions) from localStorage.
    // Hydration is lazy because the storage key depends on the current user.
    useEffect(() => {
        hydrateMediaDockHistory()
        setSnapshot(getMediaDockSnapshot())
    }, [])

    // Hand the track to the dock's own element. Resume handoffs carry the
    // live seek position; playlist navigation can opt into a fresh start.
    const adoptTrack = useCallback((mediaKey, options = {}) => {
        const startAtBeginning = options.startAtBeginning === true
        const hosted = options.hosted === true
        const owner = findMountedOwnerForMediaKey(mediaKey)
        const ownerSnapshot = getMediaOwnerPlaybackSnapshot(owner)
        const track = getMediaTrack(mediaKey)
        const canKeepSourcePlaying = Boolean(
            track?.kind === 'youtube' && owner && owner.ownerId !== dockOwnerIdRef.current,
        )
        // Join (or begin) the coordinator's handoff record so the dock
        // element's eventual claim can be token-validated. Re-adoptions of the
        // same media (e.g. the source unmounting mid-handoff) reuse the
        // existing token instead of restarting the transition.
        const handoffToken =
            track?.kind === 'youtube'
                ? beginDockMediaHandoff(mediaKey, {
                      sourceOwnerId: canKeepSourcePlaying ? owner?.ownerId : null,
                      keepSourcePlaying: canKeepSourcePlaying,
                  })
                : null
        const candidateTakeover = {
            mediaKey,
            resumeToken: Date.now(),
            handoffToken,
        }
        const deferInitialOwnerPause = isDeferredDockTakeover({
            takeover: candidateTakeover,
            handoff: getMediaHandoffInfo(),
        })
        if (startAtBeginning) {
            seekMediaKey(mediaKey, 0)
        } else if (owner && ownerSnapshot.currentTime > 0) {
            setMediaPlaybackState(mediaKey, {
                currentTime: ownerSnapshot.currentTime,
                ...(ownerSnapshot.duration > 0 ? { duration: ownerSnapshot.duration } : {}),
            })
        }
        // Native media can swap to the dock immediately. YouTube needs a new
        // iframe; keep the inline owner sounding until the dock iframe reaches
        // playing, otherwise the handoff becomes a silent buffering gap.
        if (!deferInitialOwnerPause) {
            dockPauseIntentRef.current = mediaKey
            pauseMediaKey(mediaKey, dockOwnerIdRef.current)
        }
        focusMediaDockTrack(mediaKey, { emit: false })
        setSnapshot(getMediaDockSnapshot())
        // The X hides the dock; it never stops sounding media from docking.
        setIsClosed(false)
        setTakeover(previousTakeover =>
            getTakeoverAfterAdoption({
                takeover: previousTakeover,
                candidate: candidateTakeover,
            }),
        )
        // A hosted play starts the dock's runtime directly (no inline player
        // to hand off from); keep it distinct in analytics.
        logMediaEvent(hosted ? 'MEDIA_DOCK_HOSTED_PLAY' : 'MEDIA_DOCK_HANDOFF', {
            media_key: mediaKey,
            kind: track?.kind || null,
        })
    }, [])

    // Adopt a playing song; an inline station play (the Music header's tuner)
    // comes in as the radio: Radio tab, following the station. The marker is
    // read first, since adopting pauses the inline copy and that ends the
    // header's session.
    const adoptTrackAsRadioIfInline = useCallback(
        mediaKey => {
            const inlineRadio =
                radioFeatureRef.current &&
                !playbackModesRef.current.radio &&
                isInlineRadioKey(mediaKey)
            adoptTrack(mediaKey)
            if (inlineRadio) {
                setActiveTab('radio')
                startRadioRef.current?.('inline_radio_handoff')
            }
        },
        [adoptTrack],
    )

    // A system pause (e.g. panel navigation) often lands while the origin is
    // still mid-transition on screen, so a single visibility check at pause
    // time misses it. Watch the origin closely for a moment and adopt the
    // instant it leaves the screen; if it never does, it was a real pause.
    const adoptionWatchRef = useRef(null) // { mediaKey, timer }

    const cancelAdoptionWatch = useCallback(() => {
        if (adoptionWatchRef.current?.timer) {
            window.clearTimeout(adoptionWatchRef.current.timer)
        }
        adoptionWatchRef.current = null
    }, [])

    const watchForAdoption = useCallback(
        mediaKey => {
            cancelAdoptionWatch()
            const startedAt = Date.now()

            const check = () => {
                adoptionWatchRef.current = null

                if (latestRef.current.takeoverKey === mediaKey) return

                const current = getMediaDockSnapshot()
                if (current.nowPlayingMediaKey !== mediaKey || current.isPlaying) return

                const visibleOrigin = getVisibleOrigin(mediaKey)
                if (!visibleOrigin) {
                    adoptTrackAsRadioIfInline(mediaKey)
                    return
                }

                captureOriginRect(mediaKey, visibleOrigin.rect)
                if (Date.now() - startedAt < ADOPTION_WATCH_WINDOW_MS) {
                    adoptionWatchRef.current = {
                        mediaKey,
                        timer: window.setTimeout(check, ADOPTION_WATCH_TICK_MS),
                    }
                }
            }

            check()
        },
        [adoptTrackAsRadioIfInline, cancelAdoptionWatch, captureOriginRect, getVisibleOrigin],
    )

    useEffect(() => cancelAdoptionWatch, [cancelAdoptionWatch])

    const openPlaylistFromRequest = useCallback(
        request => {
            const requestedSpecificTrack = Boolean(request?.mediaKey)
            const dockWasActive =
                !latestRef.current.isClosed &&
                (latestRef.current.isLaunched || latestRef.current.takeoverKey)
            const current = getMediaDockSnapshot()
            if (
                !requestedSpecificTrack &&
                radioFeatureRef.current &&
                !playbackModesRef.current.radio &&
                isInlineRadioPlaying(current)
            ) {
                openRadioFromRequestRef.current?.(request)
                return true
            }
            const focusMediaKey =
                request?.mediaKey ||
                current.nowPlayingMediaKey ||
                current.playlist[0]?.mediaKey ||
                current.history[0]?.mediaKey ||
                null

            if (!current.nowPlayingTrack && focusMediaKey) {
                focusMediaDockTrack(focusMediaKey, { emit: false })
            }

            setIsClosed(false)
            expandDock()
            setActiveTab(
                !requestedSpecificTrack && radioFeatureRef.current && playbackModesRef.current.radio
                    ? 'radio'
                    : 'playlist',
            )
            setIsLaunched(true)

            if (
                shouldAdoptCurrentMediaOnPlaylistOpen({
                    source: request?.source,
                    focusMediaKey,
                    hasFocusedTrack: Boolean(focusMediaKey && getMediaTrack(focusMediaKey)),
                    nowPlayingMediaKey: current.nowPlayingMediaKey,
                    isPlaying: current.isPlaying,
                    takeoverKey: latestRef.current.takeoverKey,
                })
            ) {
                adoptTrack(focusMediaKey)
            }

            const nextSnapshot = getMediaDockSnapshot()
            const focusedTrack = Boolean(nextSnapshot.nowPlayingTrack)

            if (focusedTrack && (request?.source !== 'filter_options' || dockWasActive)) {
                triggerDockAttention()
            }
            return !requestedSpecificTrack || focusedTrack
        },
        [adoptTrack, expandDock, triggerDockAttention],
    )

    const requestPlaylistFocus = useCallback(
        request => {
            const nextRequest = {
                mediaKey: request?.mediaKey || null,
                source: request?.source || null,
                requestedAt: Date.now(),
            }

            syncMediaDockPlaylistFromServer({ force: true })

            if (openPlaylistFromRequest(nextRequest)) {
                pendingPlaylistFocusRef.current = null
                return
            }

            pendingPlaylistFocusRef.current = nextRequest
        },
        [openPlaylistFromRequest],
    )

    const openPendingPlaylistFocus = useCallback(() => {
        const pendingRequest = pendingPlaylistFocusRef.current
        if (!pendingRequest) return

        if (Date.now() - pendingRequest.requestedAt > PLAYLIST_FOCUS_PENDING_MS) {
            pendingPlaylistFocusRef.current = null
            return
        }

        if (openPlaylistFromRequest(pendingRequest)) {
            pendingPlaylistFocusRef.current = null
        }
    }, [openPlaylistFromRequest])

    // An inline surface asks the dock to play with the dock's own runtime
    // placed over its origin frame. A runtime the dock already holds for that
    // track ("Play here", a re-tap) is re-parked and resumed, never rebuilt.
    const handleHostedPlayRequest = useCallback(
        event => {
            const { mediaKey, originEl, startAtBeginning, placement = 'visual' } = event
            if (!mediaKey) return

            if (playbackModesRef.current.radio) stopRadioRef.current?.('hosted_play')

            const snapshot = getMediaDockSnapshot()
            pendingHostedPlayRef.current =
                snapshot.nowPlayingMediaKey === mediaKey && snapshot.isPlaying ? null : mediaKey

            cancelAdoptionWatch()
            dockPauseIntentRef.current = null
            setIsClosed(false)
            // Audio surfaces pass no frame — the coordinator resolves the
            // track's registered origin — so always try to attach.
            const placed = enterHostedPlacement(mediaKey, originEl, placement)
            if (!placed && hostedPlacementRef.current) {
                leaveHostedPlacement('request-offscreen', { keepRuntime: true })
            }

            if (latestRef.current.takeoverKey === mediaKey && !startAtBeginning) {
                // The dock already holds this runtime (a re-tap, "Play here",
                // or resuming a silently hosted track): never rebuild it.
                // Only a placement the user can see keeps the dock hidden;
                // otherwise it belongs in the corner.
                if (!placed) setIsLaunched(true)
                if (!getMediaDockSnapshot().isPlaying) {
                    playMediaKey(mediaKey)
                }
                return
            }

            adoptTrack(mediaKey, { startAtBeginning: startAtBeginning === true, hosted: true })
        },
        [adoptTrack, cancelAdoptionWatch, enterHostedPlacement, leaveHostedPlacement],
    )

    // Publish "tuned in" for surfaces outside the dock (the ON AIR card's
    // tuner): radio mode, with the dock open.
    useEffect(() => {
        setMediaDockRadioActive(radioEnabled && !isClosed)
        return () => setMediaDockRadioActive(false)
    }, [radioEnabled, isClosed])

    useEffect(() => {
        return subscribeToMediaDock(event => {
            const isDockOwned = event.ownerId === dockOwnerIdRef.current

            if (event.type === 'answerPlaylistCounts') {
                // The server's count after this viewer's playlist change; every
                // player of the post reads it from the cached answer.
                Object.entries(event.counts).forEach(([answerId, count]) => {
                    const answer = getCachedAnswer(answerId)
                    if (answer) cacheAnswer({ ...answer, playlist_count: Number(count) || 0 })
                })
            } else if (event.type === 'hostedOriginWillClear') {
                const current = hostedPlacementRef.current
                if (current?.mediaKey === event.mediaKey && current.originEl === event.originEl) {
                    leaveHostedPlacement('origin-lost')
                }
            } else if (event.type === 'claim') {
                pendingHostedPlayRef.current = null
                dockPauseIntentRef.current = null
                cancelAdoptionWatch()
                // Something is actually playing again; the dead-source skip
                // chain starts over.
                radioErrorSkipsRef.current = 0
                // The still-sounding source of an in-flight handoff keeps
                // emitting claim echoes; resetting the takeover and preload
                // pipeline on those would restart the very transition they
                // belong to.
                const handoff = getMediaHandoffInfo()
                const isHandoffSourceEcho = Boolean(
                    handoff &&
                        event.mediaKey === handoff.mediaKey &&
                        event.ownerId === handoff.sourceOwnerId,
                )
                if (!isDockOwned && !isHandoffSourceEcho) {
                    // Claims are deflected while the dock plays, so an inline
                    // claim means the dock was idle: this is a fresh inline
                    // play. Reset the dock — it relaunches only when the
                    // media goes offscreen.
                    if (hostedPlacementRef.current) {
                        leaveHostedPlacement('inline-claim', { keepRuntime: true })
                    }
                    setIsClosed(false)
                    setIsLaunched(false)
                    setTakeover(null)
                    setYoutubePreloadReadyMediaKey(null)
                    setYoutubePreloadSyncToken(token => token + 1)
                    originOnScreenRef.current = { mediaKey: event.mediaKey, onScreen: true }

                    // An organic inline play (not radio's own spin) while the
                    // dock is idle ends the radio session.
                    if (
                        playbackModesRef.current.radio &&
                        (!navigatingRef.current ||
                            event.mediaKey !== radioLiveRef.current?.now?.track?.mediaKey)
                    ) {
                        stopRadioRef.current?.('inline_claim')
                    }
                }
                if (navigatingRef.current) {
                    navigatingRef.current = false
                } else {
                    setNavQueue(null)
                }

                // Record each distinct play as an indexable answer Action. A
                // hosted runtime parked over its post counts as an inline play.
                if (event.mediaKey !== lastPlayRecordedKeyRef.current) {
                    lastPlayRecordedKeyRef.current = event.mediaKey
                    const isHostedInlinePlay =
                        hostedPlacementRef.current?.mediaKey === event.mediaKey
                    recordMediaPlay(
                        event.mediaKey,
                        isDockOwned && !isHostedInlinePlay ? 'media_dock' : 'inline',
                    )
                }
            } else if (event.type === 'claimDeflected') {
                // The coordinator stopped an inline player because the dock is
                // playing; bring the requested track into the dock instead.
                if (!latestRef.current.isClosed) {
                    dockPauseIntentRef.current = null
                    cancelAdoptionWatch()
                    if (latestRef.current.takeoverKey !== event.mediaKey) {
                        if (playbackModesRef.current.radio) {
                            stopRadioRef.current?.('inline_play')
                        }
                        adoptTrack(event.mediaKey)
                    }
                    setNavQueue(null)
                }
            } else if (
                (event.type === 'ownerLost' || event.type === 'ownerOffscreen') &&
                !isDockOwned
            ) {
                // The playing inline element unmounted or left the active
                // visible panel. Adopt it so audio/video keeps going while
                // the user navigates or scrolls. An owner loss carrying the
                // active child-panel inline token belongs to that transition;
                // the destination will claim it without ever starting the dock.
                if (
                    shouldAdoptOwnerLifecycleEvent({
                        event,
                        handoff: getMediaHandoffInfo(),
                    })
                ) {
                    adoptTrackAsRadioIfInline(event.mediaKey)
                }
            } else if (event.type === 'release') {
                if (isDockOwned && pendingHostedPlayRef.current === event.mediaKey) {
                    pendingHostedPlayRef.current = null
                }

                // An inline player deliberately took this track off the dock
                // ("Play here"). Let go of it: keep holding the takeover and
                // the next radio tick adopts the track straight back out of
                // the player the listener just moved it to.
                if (event.takenOverByOwnerId && !event.ended) {
                    if (playbackModesRef.current.radio) {
                        stopRadioRef.current?.('inline_takeover')
                    }
                    if (latestRef.current.takeoverKey === event.mediaKey) {
                        setTakeover(null)
                    }
                    setSnapshot(getMediaDockSnapshot())
                    return
                }

                if (event.ended) {
                    // Radio owns the auto-advance while it is on the air:
                    // report the finish so the station closes this slot and
                    // draws the next play at once, then re-join the broadcast;
                    // otherwise the playlist plays on as before.
                    const radioAdvanced =
                        playbackModesRef.current.radio &&
                        !latestRef.current.isClosed &&
                        getMediaDockSnapshot().nowPlayingMediaKey === event.mediaKey &&
                        syncRadioLiveRef.current?.('radio_autoplay', {
                            holdMediaKey: event.mediaKey,
                            // What the file really measured, so the station can
                            // schedule this track accurately next time around.
                            finishedDuration: getMediaPlaybackState(event.mediaKey)?.duration,
                        })

                    // Tuned in, the station owns what plays next: never fall
                    // through to the playlist, even when the resync had to
                    // hold (dead air) or the ended key was not the dock's.
                    // An inline station play (the Music header's tuner) ending
                    // is the station's cue too; the header reports it.
                    if (
                        !radioAdvanced &&
                        !playbackModesRef.current.radio &&
                        !isInlineRadioKey(event.mediaKey)
                    ) {
                        const nextPlaylistTrack = selectNextPlaylistTrack(event.mediaKey, {
                            requireCurrent: true,
                        })
                        if (nextPlaylistTrack && playTrackRef.current) {
                            navigatingRef.current = true
                            playTrackRef.current(nextPlaylistTrack, 'autoplay')
                        }
                    }
                }

                // Only system pauses caused by offscreen panel navigation opt
                // into adoption; a user pause followed by scroll/swipe should
                // stay paused and never launch the dock.
                if (!isDockOwned) {
                    const wasDockPause = dockPauseIntentRef.current === event.mediaKey
                    dockPauseIntentRef.current = null

                    if (
                        shouldWatchReleaseForAdoption({
                            event,
                            isDockOwned,
                            wasDockPause,
                            takeoverKey: latestRef.current.takeoverKey,
                        })
                    ) {
                        watchForAdoption(event.mediaKey)
                    }
                }
            } else if (event.type === 'handoffCancelled') {
                // A cancelled takeover can never claim with its retired token.
                // Clear it even when this dock session launched an earlier
                // track, or the stale YouTube iframe will pause on every retry.
                setTakeover(previousTakeover =>
                    getTakeoverAfterHandoffCancellation({
                        takeover: previousTakeover,
                        cancelledMediaKey: event.mediaKey,
                        cancelledHandoffToken: event.handoffToken,
                    }),
                )
            } else if (event.type === 'command' && event.command === 'hostedPlay') {
                handleHostedPlayRequest(event)
            } else if (event.type === 'command' && event.command === 'focusPlaylist') {
                requestPlaylistFocus(event)
            } else if (event.type === 'command' && event.command === 'focusHistory') {
                openHistoryFromRequestRef.current?.(event)
            } else if (event.type === 'command' && event.command === 'focusRadio') {
                openRadioFromRequestRef.current?.(event)
            } else if (event.type === 'command' && event.command === 'playInDock') {
                playInDockFromRequestRef.current?.(event)
            } else if (
                event.type === 'command' &&
                event.command === 'cinema' &&
                cinemaFeatureEnabledRef.current
            ) {
                openCinemaFromRequestRef.current?.(event)
            } else if (event.type === 'command' && event.command === 'stopRadio') {
                // A tuner outside the dock (the ON AIR card) tuning out: leave
                // the broadcast and go quiet for this listener. The station
                // keeps playing without them.
                if (playbackModesRef.current.radio) {
                    stopRadioRef.current?.(event.source || 'command')
                    const current = getMediaDockSnapshot()
                    if (current.isPlaying && current.nowPlayingMediaKey) {
                        dockPauseIntentRef.current = current.nowPlayingMediaKey
                        pauseMediaKey(current.nowPlayingMediaKey)
                    }
                }
            } else if (event.type === 'command' && event.command === 'playTrack') {
                // A card's play button: adopt the registered track like a
                // playlist row would.
                const requested = getMediaTrack(event.mediaKey)
                if (requested) playTrackRef.current?.(requested, event.source || 'card')
            } else if (event.type === 'playlist' || event.type === 'history') {
                openPendingPlaylistFocus()
            }

            setSnapshot(getMediaDockSnapshot())
        })
    }, [
        adoptTrack,
        cancelAdoptionWatch,
        handleHostedPlayRequest,
        leaveHostedPlacement,
        selectNextPlaylistTrack,
        openPendingPlaylistFocus,
        requestPlaylistFocus,
        watchForAdoption,
        adoptTrackAsRadioIfInline,
    ])

    useEffect(() => {
        if (!isLanding) return undefined
        const timeout = window.setTimeout(() => setIsLanding(false), LANDING_DURATION_MS)
        return () => window.clearTimeout(timeout)
    }, [isLanding])

    const nowPlayingKey = snapshot.nowPlayingMediaKey
    const takeoverMediaKey = takeover?.mediaKey || null

    const togglePlaybackMode = useCallback(
        mode => {
            setPlaybackModes(previous => ({
                ...previous,
                [mode]: !previous[mode],
            }))

            if (mode === 'shuffle') {
                seedShufflePlayback(nowPlayingKey)
            }
        },
        [nowPlayingKey, seedShufflePlayback],
    )

    useEffect(() => {
        const currentPlaylist = snapshot.playlist || []
        if (!shuffleEnabled || !nowPlayingKey) return
        if (!currentPlaylist.some(item => item.mediaKey === nowPlayingKey)) return

        if (!shufflePlayedKeysRef.current.includes(nowPlayingKey)) {
            shufflePlayedKeysRef.current = uniqueKeys([
                ...shufflePlayedKeysRef.current,
                nowPlayingKey,
            ])
        }

        if (currentPlaylist[0]?.mediaKey === nowPlayingKey) return
        if (moveMediaTrackToPlaylistTop(nowPlayingKey)) {
            setSnapshot(getMediaDockSnapshot())
        }
    }, [nowPlayingKey, snapshot.playlist, shuffleEnabled])

    useEffect(() => {
        setYoutubePreloadReadyMediaKey(null)
        // Assume the new track's inline player is on screen (it usually is
        // at the moment of the play click); the poll corrects this within a
        // tick.
        originOnScreenRef.current = { mediaKey: nowPlayingKey, onScreen: true }
    }, [nowPlayingKey])

    const syncVolumeFromPlaybackState = useCallback(playbackState => {
        const nextVolume = clampDockVolume(playbackState?.volume)
        if (nextVolume == null) return

        volumeRef.current = nextVolume
        setVolume(previousVolume =>
            Math.abs(previousVolume - nextVolume) > 0.001 ? nextVolume : previousVolume,
        )
    }, [])

    useEffect(() => {
        if (!nowPlayingKey) return undefined

        const playbackState = getMediaPlaybackState(nowPlayingKey)
        syncVolumeFromPlaybackState(playbackState)
        setMediaKeyVolume(
            nowPlayingKey,
            clampDockVolume(playbackState?.volume) ?? volumeRef.current,
            { forceOwnerSync: true },
        )
        return subscribeToMediaPlaybackState(nowPlayingKey, syncVolumeFromPlaybackState)
    }, [nowPlayingKey, syncVolumeFromPlaybackState])

    useEffect(() => {
        if (!takeoverMediaKey) return
        setMediaKeyVolume(takeoverMediaKey, volumeRef.current, { forceOwnerSync: true })
    }, [takeoverMediaKey])

    // Track whether the inline origin is still what the user is looking at,
    // handing playing media to the dock the moment it stops being so. Playback
    // progress is isolated in MediaDockProgress and follows native media events.
    useEffect(() => {
        if (!nowPlayingKey) return undefined
        if (!isDocumentVisible) return undefined

        const update = () => {
            incrementMediaDockQaCounter('dock.pollTicks')
            syncDockPlacement()

            if (latestRef.current.takeoverKey !== nowPlayingKey) {
                const visibleOrigin = getVisibleOrigin(nowPlayingKey)
                const onScreen = Boolean(visibleOrigin)
                if (visibleOrigin) {
                    captureOriginRect(nowPlayingKey, visibleOrigin.rect)
                }

                const wasOnScreen =
                    originOnScreenRef.current.mediaKey === nowPlayingKey &&
                    originOnScreenRef.current.onScreen
                originOnScreenRef.current = { mediaKey: nowPlayingKey, onScreen }
                const currentDockSnapshot = getMediaDockSnapshot()

                if (
                    wasOnScreen &&
                    !onScreen &&
                    currentDockSnapshot.nowPlayingMediaKey === nowPlayingKey &&
                    currentDockSnapshot.isPlaying &&
                    // A child-thread inline keep deliberately plays offscreen
                    // during the slide; the dock must not adopt out from
                    // under that transition.
                    !isInlineMediaPlaybackHandoffPending(nowPlayingKey)
                ) {
                    // Once docked, only the dock plays: adopt audio and video
                    // alike so the inline element never keeps sounding.
                    adoptTrackAsRadioIfInline(nowPlayingKey)
                }
            } else if (dockHiddenPreparingRef.current) {
                // The takeover is still hidden behind the reveal gate while
                // the inline source keeps sounding. If its origin has stably
                // returned to view (two ticks, past the swipe animation), the
                // user is watching the inline player again — abandon the
                // takeover instead of stealing playback when the gate opens.
                const visibleOrigin = getVisibleOrigin(nowPlayingKey)
                if (visibleOrigin) {
                    const previousReturn = originReturnRef.current
                    const ticks =
                        previousReturn?.mediaKey === nowPlayingKey ? previousReturn.ticks + 1 : 1
                    originReturnRef.current = { mediaKey: nowPlayingKey, ticks }
                    if (
                        ticks >= 2 &&
                        Date.now() - latestRef.current.takeoverResumeToken > 1200 &&
                        cancelMediaHandoffForVisibleSource(nowPlayingKey)
                    ) {
                        originReturnRef.current = null
                    }
                } else {
                    originReturnRef.current = null
                }
            }
        }

        update()
        const intervalMs = getMediaDockPollIntervalMs({
            isPlaying: snapshot.isPlaying,
            isDocumentVisible,
            isAwaitingHandoff: takeoverMediaKey !== nowPlayingKey,
            isExpanded,
            isDraggingSeek,
        })
        if (intervalMs == null) return undefined
        const interval = window.setInterval(update, intervalMs)
        return () => window.clearInterval(interval)
    }, [
        adoptTrackAsRadioIfInline,
        nowPlayingKey,
        takeoverMediaKey,
        snapshot.isPlaying,
        isDocumentVisible,
        isExpanded,
        isDraggingSeek,
        adoptTrack,
        captureOriginRect,
        getVisibleOrigin,
        syncDockPlacement,
    ])

    const handleAutoplayBlocked = useCallback(() => {
        pendingHostedPlayRef.current = null
        setSnapshot(getMediaDockSnapshot())
    }, [])

    const playTrack = useCallback(
        (track, source = 'dock', options = {}) => {
            if (!track?.mediaKey) return

            // Choosing media leaves the broadcast, including while a station
            // fetch is in flight. Its ended event belongs to the playlist.
            if (playbackModesRef.current.radio) stopRadioRef.current?.(source)

            if (!['autoplay', 'next'].includes(source)) {
                seedShufflePlayback(track.mediaKey)
            }

            const startAtBeginning = shouldStartTrackAtBeginning(source, options)
            logMediaEvent('MEDIA_DOCK_PLAY', { media_key: track.mediaKey, source })

            // If the dock already owns this track, refresh its element at the
            // requested playback position.
            if (latestRef.current.takeoverKey === track.mediaKey) {
                if (startAtBeginning) {
                    seekMediaKey(track.mediaKey, 0)
                }
                setTakeover(previousTakeover => ({
                    ...previousTakeover,
                    mediaKey: track.mediaKey,
                    resumeToken: Date.now(),
                }))
                return
            }

            // Only hand playback to the inline player while it is on screen;
            // otherwise play it in the dock. Cinema covers every post, so a
            // playlist advancing under it stays on the stage.
            const visibleOrigin = isCinemaRef.current ? null : getVisibleOrigin(track.mediaKey)
            if (visibleOrigin && playMediaKey(track.mediaKey, { startAtBeginning })) {
                return
            }

            // Hosted tracks have no inline player of their own: park the
            // dock's runtime over the visible origin instead.
            if (
                visibleOrigin &&
                hostedPlaybackEnabledRef.current &&
                getMediaTrack(track.mediaKey)?.kind === 'youtube'
            ) {
                enterHostedPlacement(track.mediaKey, visibleOrigin.element)
            } else if (hostedPlacementRef.current) {
                leaveHostedPlacement('track-changed', { keepRuntime: true })
            }

            adoptTrack(track.mediaKey, { startAtBeginning })
        },
        [
            adoptTrack,
            enterHostedPlacement,
            getVisibleOrigin,
            leaveHostedPlacement,
            seedShufflePlayback,
        ],
    )
    // --- Treechat Radio -----------------------------------------------------

    // A persisted radio mode without the feature flag (rollout revoked) must
    // not keep steering playback.
    useEffect(() => {
        if (!radioFeatureEnabled && playbackModes.radio) {
            playbackModesRef.current = { ...playbackModesRef.current, radio: false }
            setPlaybackModes(previous => ({ ...previous, radio: false }))
        }
    }, [radioFeatureEnabled, playbackModes.radio])

    playTrackRef.current = playTrack

    const ensureRadioPool = useCallback((options = {}) => {
        setRadioPool(previous =>
            previous.tracks.length ? previous : { ...previous, status: 'loading' },
        )
        return fetchRadioPool(options).then(tracks => {
            setRadioPool({
                status: tracks.length ? 'ready' : 'empty',
                tracks,
                odds: getRadioPoolOdds(),
            })
            return tracks
        })
    }, [])

    // The shared station clock owns rollover timing; the dock only records
    // whether playback is waiting for a different slot.
    const clearRadioWait = useCallback(() => {
        radioWaitingRef.current = false
    }, [])
    const waitForRadioSlot = useCallback(() => {
        radioWaitingRef.current = true
    }, [])

    // Tunes playback to the live broadcast: fetch what the station is playing
    // right now and seek to that position. Returns true (the sync is at least
    // in flight) so ended-handlers skip the playlist auto-advance.
    const syncRadioLive = useCallback(
        (source = 'radio_sync', options = {}) => {
            const generation = ++radioSyncGenerationRef.current
            // The song we were playing actually reached its end: tell the
            // station so it closes the slot and draws the next play now,
            // instead of every listener sitting in dead air until a padded
            // slot (an unmeasured track schedules a fallback length) expires.
            const endedLive =
                options.holdMediaKey &&
                radioLiveRef.current?.now?.track?.mediaKey === options.holdMediaKey
                    ? radioLiveRef.current.now
                    : null
            // Captured now: radioLiveRef is overwritten with the response below.
            const endedReceivedAtMs = radioLiveRef.current?.receivedAtMs
            const request = options.live
                ? Promise.resolve(options.live)
                : endedLive
                ? finishRadioNow({
                      playIndex: endedLive.playIndex,
                      duration: options.finishedDuration,
                  }).then(payload => radioNowFeed.publish(payload))
                : radioNowFeed.refresh({ listening: true })

            request.then(live => {
                if (generation !== radioSyncGenerationRef.current) return
                if (!playbackModesRef.current.radio) return
                if (latestRef.current.isClosed) return

                radioLiveRef.current = live
                setRadioLive(live)

                // A listener may pause while the socket-triggered read is in
                // flight. Keep that pause, but still recover a waiting slot.
                if (
                    source === 'radio_socket' &&
                    latestRef.current.takeoverKey &&
                    !getMediaDockSnapshot().isPlaying &&
                    !radioWaitingRef.current
                ) {
                    return
                }

                const liveTrack = live?.now?.track
                if (!liveTrack?.mediaKey) {
                    waitForRadioSlot()
                    // The station has nothing left to air (an admin skipped its last
                    // eligible song): stop the one we were still playing.
                    const current = getMediaDockSnapshot()
                    if (
                        source === 'radio_socket' &&
                        current.isPlaying &&
                        current.nowPlayingMediaKey
                    ) {
                        pauseMediaKey(current.nowPlayingMediaKey)
                    }
                    return
                }

                // Still the same play we just heard end — the finish report
                // did not land (offline, or the station refused it). Replaying
                // it would loop ended→sync forever, so hold and re-join at slot
                // end. A new slot that drew the same song (a pool small enough
                // to outrun the cooldown) is a real play, and does replay.
                const endedPlayIndex = endedLive?.playIndex
                const sameSong = options.holdMediaKey && options.holdMediaKey === liveTrack.mediaKey
                const samePlay = endedPlayIndex == null || live.now.playIndex === endedPlayIndex
                if (sameSong && samePlay) {
                    waitForRadioSlot()
                    // The station refuses an early finish report for an unmeasured
                    // track until its minimum airtime has passed: report once more
                    // after that, rather than leaving a short song's slot to run out.
                    if (endedLive && !options.finishRetry) {
                        const retryDelay = radioFinishRetryDelayMs({
                            position: endedLive.position,
                            receivedAtMs: endedReceivedAtMs,
                        })
                        setTimeout(() => {
                            if (
                                radioWaitingRef.current &&
                                playbackModesRef.current.radio &&
                                !latestRef.current.isClosed
                            ) {
                                syncRadioLiveRef.current?.(source, {
                                    ...options,
                                    finishRetry: true,
                                })
                            }
                        }, retryDelay)
                    }
                    return
                }

                clearRadioWait()
                const livePosition = liveRadioPosition({
                    position: live.now.position,
                    serverNow: live.serverNow,
                    duration: live.now.duration,
                })

                logMediaEvent('MEDIA_DOCK_RADIO_SYNC', {
                    media_key: liveTrack.mediaKey,
                    source,
                    position: Math.round(livePosition),
                })
                navigatingRef.current = true

                if (latestRef.current.takeoverKey === liveTrack.mediaKey) {
                    seekMediaKey(liveTrack.mediaKey, livePosition)
                    playMediaKey(liveTrack.mediaKey)
                } else {
                    adoptTrack(liveTrack.mediaKey)
                    // After adoption so the origin snapshot cannot clobber it:
                    // the dock element mounts later and resumes from here.
                    setMediaPlaybackState(liveTrack.mediaKey, { currentTime: livePosition })
                }
            })
            return true
        },
        [adoptTrack, clearRadioWait, waitForRadioSlot],
    )
    syncRadioLiveRef.current = syncRadioLive

    useEffect(() => {
        const radioTabOpen = isExpanded && activeTab === 'radio'
        if (!radioFeatureEnabled || (!radioEnabled && !radioTabOpen)) {
            return undefined
        }
        return subscribeRadioNow(
            live => {
                if (!live) return
                const previous = radioLiveRef.current?.now
                const changed =
                    previous?.playIndex !== live.now?.playIndex ||
                    previous?.endsAt !== live.now?.endsAt
                radioLiveRef.current = live
                setRadioLive(live)
                const current = getMediaDockSnapshot()
                if (
                    changed &&
                    playbackModesRef.current.radio &&
                    !latestRef.current.isClosed &&
                    (current.isPlaying || !latestRef.current.takeoverKey || radioWaitingRef.current)
                )
                    syncRadioLiveRef.current?.('radio_socket', { live })
            },
            { listening: radioEnabled && snapshot.isPlaying },
        )
    }, [radioFeatureEnabled, radioEnabled, isExpanded, activeTab, snapshot.isPlaying])

    const startRadio = useCallback(
        (source = 'radio_tab') => {
            if (!radioFeatureRef.current) return

            logMediaEvent('MEDIA_DOCK_RADIO_START', { source })
            playbackModesRef.current = { ...playbackModesRef.current, radio: true }
            setPlaybackModes(previous => ({ ...previous, radio: true }))
            syncRadioLive(source)
        },
        [syncRadioLive],
    )

    const stopRadio = useCallback(
        (source = 'radio_tab') => {
            radioSyncGenerationRef.current += 1
            clearInlineRadioPlay()
            logMediaEvent('MEDIA_DOCK_RADIO_STOP', { source })
            playbackModesRef.current = { ...playbackModesRef.current, radio: false }
            setPlaybackModes(previous => ({ ...previous, radio: false }))
            clearRadioWait()
        },
        [clearRadioWait],
    )
    stopRadioRef.current = stopRadio
    startRadioRef.current = startRadio

    // A dead source (deleted file, expired URL) never fires "ended", which
    // would strand the broadcast locally; hold (dead air) and re-join when
    // the slot rolls over. The cap stops a fully broken pool from retrying
    // forever.
    const handleDockMediaError = useCallback(
        mediaKey => {
            setSnapshot(getMediaDockSnapshot())
            if (!playbackModesRef.current.radio) return
            if (latestRef.current.isClosed) return
            if (latestRef.current.takeoverKey !== mediaKey) return

            radioErrorSkipsRef.current += 1
            if (radioErrorSkipsRef.current > 10) return

            logMediaEvent('MEDIA_DOCK_RADIO_SKIP_ERROR', { media_key: mediaKey })
            waitForRadioSlot()
        },
        [waitForRadioSlot],
    )

    // Keep the pool warm whenever radio mode is on or its tab is visible.
    useEffect(() => {
        if (radioEnabled || (isExpanded && activeTab === 'radio')) {
            ensureRadioPool()
        }
    }, [radioEnabled, isExpanded, activeTab, ensureRadioPool])

    // The tab shows the next draw's odds, which change with every play:
    // reread them whenever a new song goes on air while the tab is open.
    const radioPlayIndex = radioLive?.now?.playIndex ?? null
    useEffect(() => {
        if (isExpanded && activeTab === 'radio' && radioPlayIndex != null) {
            ensureRadioPool({ force: true })
        }
    }, [radioPlayIndex, isExpanded, activeTab, ensureRadioPool])

    const toggleBroadcastPlays = useCallback(() => {
        setBroadcastPlays(previous => {
            const next = !previous
            setNowPlayingBroadcastEnabled(next)
            logMediaEvent('MEDIA_DOCK_BROADCAST_TOGGLE', { enabled: next })
            return next
        })
    }, [])

    // The History tab, optionally centred on a track (an aired radio play).
    const openHistoryFromRequest = useCallback(
        request => {
            setIsClosed(false)
            expandDock()
            setActiveTab('history')
            setIsLaunched(true)

            const mediaKey = request?.mediaKey
            if (mediaKey && getMediaTrack(mediaKey)) {
                focusMediaDockTrack(mediaKey, { emit: false })
            }
            syncMediaDockPlaylistFromServer({ force: true })
            triggerDockAttention()
        },
        [expandDock, triggerDockAttention],
    )
    openHistoryFromRequestRef.current = openHistoryFromRequest

    const openRadioFromRequest = useCallback(
        request => {
            if (!radioFeatureRef.current) return

            setIsClosed(false)
            expandDock()
            setActiveTab('radio')
            setIsLaunched(true)

            const current = getMediaDockSnapshot()
            if (playbackModesRef.current.radio && current.isPlaying) {
                ensureRadioPool()
            } else {
                startRadio(request?.source || 'command')
            }
            triggerDockAttention()
        },
        [ensureRadioPool, expandDock, startRadio, triggerDockAttention],
    )
    openRadioFromRequestRef.current = openRadioFromRequest

    const handleTogglePlayback = useCallback(
        (source = 'dock') => {
            const current = getMediaDockSnapshot()
            if (!current.nowPlayingTrack) return

            if (current.isPlaying) {
                logMediaEvent('MEDIA_DOCK_PAUSE', {
                    media_key: current.nowPlayingMediaKey,
                    source,
                })
                dockPauseIntentRef.current = current.nowPlayingMediaKey
                if (playbackModesRef.current.radio) {
                    radioSyncGenerationRef.current += 1
                    clearRadioWait()
                }
                pauseMediaKey(current.nowPlayingMediaKey)
            } else if (playbackModesRef.current.radio) {
                // Live radio does not resume where it paused — it re-joins
                // the broadcast.
                syncRadioLive('radio_resume')
            } else {
                playTrack(current.nowPlayingTrack, source)
            }
        },
        [playTrack, syncRadioLive, clearRadioWait],
    )

    const handleClose = useCallback(() => {
        pendingHostedPlayRef.current = null
        cancelAdoptionWatch()
        if (playbackModesRef.current.radio) {
            stopRadio('dock_close')
        }
        const current = getMediaDockSnapshot()
        logMediaEvent('MEDIA_DOCK_CLOSE', { media_key: current.nowPlayingMediaKey })
        // Closing stops only what the dock plays. A song still playing in its
        // post (the dock was just showing the playlist) keeps going.
        if (
            current.nowPlayingMediaKey &&
            (latestRef.current.takeoverKey === current.nowPlayingMediaKey ||
                isMediaKeyPresentedInDock(current.nowPlayingMediaKey))
        ) {
            dockPauseIntentRef.current = current.nowPlayingMediaKey
            pauseMediaKey(current.nowPlayingMediaKey)
        }
        setTakeover(null)
        setIsExpanded(false)
        setIsCinema(false)
        setIsLaunched(false)
        setIsClosed(true)
    }, [cancelAdoptionWatch, stopRadio])

    // A media dropdown's "Play in Dock": the dock takes the track over and
    // opens. A radio-mode song (the tuner's inline station play, or the song
    // the radio is airing) lands on the Radio tab following the station, the
    // way adoptTrackAsRadioIfInline hands it off; anything else leaves the
    // radio and opens on the playlist's now-playing view.
    const playInDockFromRequest = useCallback(
        request => {
            const mediaKey = request?.mediaKey
            if (!mediaKey || !getMediaTrack(mediaKey)) return

            const source = request?.source || 'play_in_dock'
            const radioOn = playbackModesRef.current.radio
            const current = getMediaDockSnapshot()
            const tab = playInDockTab({
                mediaKey,
                radioFeature: radioFeatureRef.current,
                radioOn,
                nowPlayingMediaKey: current.nowPlayingMediaKey,
                inlineRadio: isInlineRadioKey(mediaKey),
            })

            cancelAdoptionWatch()
            if (tab === 'playlist' && radioOn) stopRadioRef.current?.(source)
            if (tab === 'radio' && radioOn) {
                // Live radio never resumes where it paused: it re-joins the
                // broadcast, as the dock's own play button does.
                if (!current.isPlaying) {
                    syncRadioLiveRef.current?.('radio_resume')
                } else if (latestRef.current.takeoverKey !== mediaKey) {
                    adoptTrack(mediaKey)
                }
            } else if (latestRef.current.takeoverKey === mediaKey) {
                setTakeover(previousTakeover => ({
                    ...previousTakeover,
                    mediaKey,
                    resumeToken: Date.now(),
                }))
            } else {
                adoptTrack(mediaKey)
            }

            setIsClosed(false)
            expandDock()
            setActiveTab(tab)
            setIsLaunched(true)
            if (tab === 'radio' && !radioOn) startRadioRef.current?.('inline_radio_handoff')
            logMediaEvent('MEDIA_DOCK_PLAY_IN_DOCK', { media_key: mediaKey, source, tab })
            triggerDockAttention()
        },
        [adoptTrack, cancelAdoptionWatch, expandDock, triggerDockAttention],
    )
    playInDockFromRequestRef.current = playInDockFromRequest

    const handleExpand = useCallback(() => {
        const current = getMediaDockSnapshot()
        if (
            radioFeatureRef.current &&
            !playbackModesRef.current.radio &&
            isInlineRadioPlaying(current)
        ) {
            openRadioFromRequestRef.current?.({ source: 'dock_expand' })
            return
        }
        setActiveTab(
            radioFeatureRef.current && playbackModesRef.current.radio
                ? 'radio'
                : current.playlist.length
                ? 'playlist'
                : 'history',
        )
        expandDock()
        logMediaEvent('MEDIA_DOCK_EXPAND', { media_key: current.nowPlayingMediaKey })
    }, [expandDock])

    const handleCollapse = useCallback(() => {
        setIsExpanded(false)
        setIsCinema(false)
        logMediaEvent('MEDIA_DOCK_COLLAPSE', {})
    }, [])

    // --- Cinema -------------------------------------------------------------

    // A media dropdown's "Cinema" row: show the track on the dock's stage
    // filling the app window. The dock's own runtime plays it — taken over
    // from the post's player the way "Expand player" takes it, or started
    // fresh — so the picture is one element wherever it is shown. What the
    // viewer had before is remembered so leaving cinema can restore it.
    const openCinemaFromRequest = useCallback(
        request => {
            if (!cinemaFeatureEnabledRef.current) return false

            const mediaKey = request?.mediaKey
            const track = mediaKey ? getMediaTrack(mediaKey) : null
            if (!track) return false

            if (!isCinemaRef.current) {
                const placement = hostedPlacementRef.current
                const hostedHere = placement?.mediaKey === mediaKey
                cinemaReturnRef.current = {
                    mediaKey,
                    presentation: getCinemaReturnPresentation({
                        mediaKey,
                        takeoverMediaKey: latestRef.current.takeoverKey,
                        hostedMediaKey: placement?.mediaKey,
                        isPresented: isPresentedRef.current,
                        isExpanded: latestRef.current.isExpanded,
                    }),
                    hostedPlacement: hostedHere
                        ? { originEl: placement.originEl, kind: placement.kind }
                        : null,
                    originEl: request?.originEl || (hostedHere ? placement.originEl : null),
                }
                cinemaFocusReturnRef.current = {
                    element: request?.returnFocusEl || null,
                    selector: request?.returnFocusSelector || null,
                    mediaKey,
                    originEl: cinemaReturnRef.current.originEl,
                }
            }

            // Choosing a video to watch leaves the broadcast.
            if (playbackModesRef.current.radio) stopRadioRef.current?.('cinema')
            cancelAdoptionWatch()
            dockPauseIntentRef.current = null
            setIsClosed(false)

            if (latestRef.current.takeoverKey !== mediaKey) {
                if (hostedPlacementRef.current) {
                    leaveHostedPlacement('cinema', { keepRuntime: true })
                }
                adoptTrack(mediaKey, { hosted: hostedPlaybackEnabledRef.current })
            } else if (!getMediaDockSnapshot().isPlaying) {
                // The dock holds this runtime paused: resume its own element
                // (a fresh token re-runs the dock element's resume), never a
                // post's copy — an inline claim would reset the dock.
                setTakeover(previousTakeover => ({
                    ...previousTakeover,
                    mediaKey,
                    resumeToken: Date.now(),
                }))
            }

            expandDock()
            setIsCinema(true)
            setCinemaChromeVisible(true)
            setIsLaunched(true)
            logMediaEvent('MEDIA_DOCK_CINEMA_ENTER', {
                media_key: mediaKey,
                kind: track.kind || null,
                source: request?.source || null,
            })
            return true
        },
        [adoptTrack, cancelAdoptionWatch, expandDock, leaveHostedPlacement],
    )
    openCinemaFromRequestRef.current = openCinemaFromRequest

    // Leave cinema and put the picture back where it came from: the expanded
    // dock if that is where cinema was opened from, the corner dock if it was
    // already showing, otherwise the post. A hosted runtime is re-parked over
    // the frame it was in (playing or paused — parking starts nothing); a
    // runtime the dock took over just for cinema is offered back to the
    // post's own player, which answers with its "Play here" while it is on
    // screen. That resumes, so a paused one stays with the corner dock instead,
    // the way a collapse leaves it.
    const exitCinema = useCallback(
        (reason = 'exit') => {
            if (!isCinemaRef.current) return
            const restore = cinemaReturnRef.current
            cinemaReturnRef.current = null
            setIsCinema(false)

            const mediaKey = restore?.mediaKey || null
            const current = getMediaDockSnapshot()
            logMediaEvent('MEDIA_DOCK_CINEMA_EXIT', {
                media_key: current.nowPlayingMediaKey,
                reason,
            })

            const presentation = restore?.presentation || 'corner'
            if (presentation === 'expanded') return

            setIsExpanded(false)
            const holdsRuntime = Boolean(mediaKey) && latestRef.current.takeoverKey === mediaKey
            if (presentation === 'corner' || !holdsRuntime) return

            if (
                restore.hostedPlacement &&
                enterHostedPlacement(
                    mediaKey,
                    restore.hostedPlacement.originEl,
                    restore.hostedPlacement.kind,
                )
            ) {
                return
            }
            if (
                presentation === 'none' &&
                current.isPlaying &&
                current.nowPlayingMediaKey === mediaKey &&
                restore.originEl &&
                isElementUserVisible(restore.originEl, { mediaKey })
            ) {
                setIsLaunched(false)
                announceMediaDockCinemaExit(mediaKey, restore.originEl)
            }
        },
        [enterHostedPlacement],
    )
    const exitCinemaRef = useRef(exitCinema)
    exitCinemaRef.current = exitCinema

    useLayoutEffect(() => {
        if (!isCinema || typeof document === 'undefined') return undefined
        const dockElement = rootRef.current
        const backgroundElements = []
        let branchElement = dockElement
        while (branchElement?.parentElement) {
            const parentElement = branchElement.parentElement
            Array.from(parentElement.children).forEach(element => {
                if (element === branchElement) return
                backgroundElements.push({
                    element,
                    hadInert: element.hasAttribute('inert'),
                    ariaHidden: element.getAttribute('aria-hidden'),
                })
            })
            if (parentElement === document.body) break
            branchElement = parentElement
        }

        backgroundElements.forEach(({ element }) => {
            element.setAttribute('inert', '')
            element.setAttribute('aria-hidden', 'true')
        })

        cinemaExitButtonRef.current?.focus()

        const handleKeyDown = event => {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                event.preventDefault()
                // In the browser's fullscreen Escape belongs to it: leave that
                // and stay in cinema, the way the video player's chrome does.
                if (isVideoFullscreenActive({ fullscreenTarget: dockElement })) {
                    toggleVideoFullscreen({ fullscreenTarget: dockElement })
                    return
                }
                exitCinemaRef.current('escape')
                return
            }
            if (event.key !== 'Tab' || event.defaultPrevented || !dockElement) return

            const focusable = Array.from(
                dockElement.querySelectorAll(CINEMA_FOCUSABLE_SELECTOR),
            ).filter(element => element.getClientRects().length > 0)
            if (!focusable.length) {
                event.preventDefault()
                return
            }

            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            backgroundElements.forEach(({ element, hadInert, ariaHidden }) => {
                if (!hadInert) element.removeAttribute('inert')
                if (ariaHidden == null) element.removeAttribute('aria-hidden')
                else element.setAttribute('aria-hidden', ariaHidden)
            })

            const focusReturn = cinemaFocusReturnRef.current
            cinemaFocusReturnRef.current = null
            const scheduleFrame = callback =>
                typeof window.requestAnimationFrame === 'function'
                    ? window.requestAnimationFrame(callback)
                    : window.setTimeout(callback, 0)
            // A post's chrome is drawn afresh each time it moves between the
            // post and the hosted runtime's chrome layer, so the trigger that
            // opened cinema can be a new element by now, still mounting.
            let framesLeft = CINEMA_FOCUS_RETURN_FRAMES
            const restoreFocus = () => {
                const target =
                    (focusReturn?.element?.isConnected && focusReturn.element) ||
                    (focusReturn?.selector && document.querySelector(focusReturn.selector)) ||
                    findMediaMenuTrigger(focusReturn?.mediaKey, focusReturn?.originEl)
                if (target) target.focus?.()
                else if (--framesLeft > 0) scheduleFrame(retryFocus)
            }
            // A later frame only reclaims focus that nothing else has taken.
            const retryFocus = () => {
                const active = document.activeElement
                if (!active || active === document.body) restoreFocus()
            }
            scheduleFrame(restoreFocus)
        }
    }, [isCinema])

    useEffect(() => {
        if (!cinemaFeatureEnabled && isCinema) exitCinemaRef.current('feature_disabled')
    }, [cinemaFeatureEnabled, isCinema])

    // --- Cinema settings and fullscreen -------------------------------------

    // The transport's gear reads the dock's own runtime — its speed and the
    // speeds it offers, its captions — through the coordinator's owner
    // registry, the way hosted chrome reads it from a post. Dock and playback
    // state events cover the changes the dock makes itself; the slow tick
    // catches what the runtime reports on its own schedule.
    const refreshStageRuntimeControls = useCallback(() => {
        const next = readStageRuntimeControls(nowPlayingKey, { ownerId: dockOwnerIdRef.current })
        setStageRuntimeControls(previous =>
            stageRuntimeControlsEqual(previous, next) ? previous : next,
        )
    }, [nowPlayingKey])

    useEffect(() => {
        if (!isCinema || !nowPlayingKey || typeof window === 'undefined') return undefined
        refreshStageRuntimeControls()
        const unsubscribeDock = subscribeToMediaDock(refreshStageRuntimeControls)
        const unsubscribePlayback = subscribeToMediaPlaybackState(
            nowPlayingKey,
            refreshStageRuntimeControls,
        )
        const interval = window.setInterval(refreshStageRuntimeControls, CINEMA_CONTROLS_POLL_MS)
        return () => {
            unsubscribeDock()
            unsubscribePlayback()
            window.clearInterval(interval)
        }
    }, [isCinema, nowPlayingKey, refreshStageRuntimeControls])

    const handleStagePlaybackRate = useCallback(
        rate => {
            if (!nowPlayingKey) return
            setMediaKeyPlaybackRate(nowPlayingKey, rate)
            refreshStageRuntimeControls()
        },
        [nowPlayingKey, refreshStageRuntimeControls],
    )

    const handleStageToggleCaptions = useCallback(() => {
        if (!nowPlayingKey) return
        setMediaKeyCaptionsEnabled(nowPlayingKey, !stageRuntimeControls.captionsEnabled)
        refreshStageRuntimeControls()
    }, [nowPlayingKey, refreshStageRuntimeControls, stageRuntimeControls.captionsEnabled])

    // Fullscreen from cinema is the video player's own ladder on the dock
    // root — the browser's element fullscreen, or the native iOS app's video
    // fullscreen — so the cinema chrome comes along. Cinema itself never asks
    // for fullscreen (openCinemaFromRequest); only this control does.
    const getStageFullscreenVideoElement = useCallback(() => {
        const mediaEl = findStageRuntimeOwner(nowPlayingKey, dockOwnerIdRef.current)?.mediaEl
        return typeof HTMLVideoElement !== 'undefined' && mediaEl instanceof HTMLVideoElement
            ? mediaEl
            : null
    }, [nowPlayingKey])

    const handleStageFullscreen = useCallback(() => {
        toggleVideoFullscreen({
            fullscreenTarget: rootRef.current,
            videoElement: getStageFullscreenVideoElement(),
        })
    }, [getStageFullscreenVideoElement])

    useEffect(() => {
        if (!isCinema || typeof document === 'undefined') return undefined
        const stageVideoElement = getStageFullscreenVideoElement()
        const syncFullscreen = () => {
            setIsCinemaFullscreen(
                isVideoFullscreenActive({
                    fullscreenTarget: rootRef.current,
                    videoElement: stageVideoElement,
                }),
            )
        }
        syncFullscreen()
        document.addEventListener('fullscreenchange', syncFullscreen)
        document.addEventListener('webkitfullscreenchange', syncFullscreen)
        stageVideoElement?.addEventListener('webkitbeginfullscreen', syncFullscreen)
        stageVideoElement?.addEventListener('webkitendfullscreen', syncFullscreen)
        return () => {
            document.removeEventListener('fullscreenchange', syncFullscreen)
            document.removeEventListener('webkitfullscreenchange', syncFullscreen)
            stageVideoElement?.removeEventListener('webkitbeginfullscreen', syncFullscreen)
            stageVideoElement?.removeEventListener('webkitendfullscreen', syncFullscreen)
        }
    }, [getStageFullscreenVideoElement, isCinema])

    // Leaving cinema leaves the browser's fullscreen with it, whichever way
    // cinema ended: the corner dock or the post would otherwise be shown
    // inside a fullscreen root.
    useEffect(() => {
        if (isCinema || typeof document === 'undefined') return
        const rootEl = rootRef.current
        const stageVideoElement = getStageFullscreenVideoElement()
        if (
            rootEl &&
            isVideoFullscreenActive({
                fullscreenTarget: rootEl,
                videoElement: stageVideoElement,
            })
        ) {
            toggleVideoFullscreen({ fullscreenTarget: rootEl, videoElement: stageVideoElement })
        }
        setIsCinemaFullscreen(false)
    }, [getStageFullscreenVideoElement, isCinema])

    const handleVolume = useCallback(
        event => {
            const nextVolume = clampDockVolume(event.currentTarget.value)
            if (nextVolume == null) return
            volumeRef.current = nextVolume
            setVolume(nextVolume)
            setMediaKeyVolume(nowPlayingKey, nextVolume)
        },
        [nowPlayingKey],
    )

    // The volume flyout mirrors VideoChrome: the slider is a hover/focus
    // affordance on fine pointers, so on touch the button is a mute toggle
    // that restores the last audible level.
    const lastAudibleVolumeRef = useRef(1)
    useEffect(() => {
        if (volume > 0.001) lastAudibleVolumeRef.current = volume
    }, [volume])

    const handleToggleMute = useCallback(() => {
        const nextVolume = volume <= 0.001 ? lastAudibleVolumeRef.current || 1 : 0
        volumeRef.current = nextVolume
        setVolume(nextVolume)
        setMediaKeyVolume(nowPlayingKey, nextVolume)
    }, [nowPlayingKey, volume])

    const handleTogglePlaylistMembership = useCallback((source = 'dock') => {
        const current = getMediaDockSnapshot()
        const mediaKey = current.nowPlayingMediaKey
        if (!mediaKey) return

        if (isMediaTrackInPlaylist(mediaKey)) {
            removeMediaTrackFromPlaylist(mediaKey)
            logMediaEvent('MEDIA_DOCK_REMOVE_FROM_PLAYLIST', {
                media_key: mediaKey,
                kind: current.nowPlayingTrack?.kind || null,
                answer_id: current.nowPlayingTrack?.answerId || null,
                source,
            })
        } else {
            addTrackToPlaylist(mediaKey, source, { notify: false })
        }
        setSnapshot(getMediaDockSnapshot())
    }, [])

    const ensureOrdinalTrackAnswer = useCallback(async track => {
        const artifactOrdinalId = track?.artifactOrdinalId
        if (!artifactOrdinalId || !track?.mediaKey || !track?.src) return null

        const result = await api.ensureOrdinalThread(artifactOrdinalId)
        if (!result?.answer_id) return null

        registerMediaTrack(track.mediaKey, {
            src: track.src,
            answerId: result.answer_id,
            questId: result.child_quest_id || null,
        })
        setSnapshot(getMediaDockSnapshot())
        return result
    }, [])

    // Opens the thread containing the playing answer's replies using the app's
    // mode-aware center-panel navigation.
    const handleShowInThread = useCallback(async () => {
        const current = getMediaDockSnapshot()
        const track = current.nowPlayingTrack
        let answerId = track?.answerId
        let ensuredThread = null

        if (!answerId && track?.artifactOrdinalId) {
            if (!gon?.currentUser) {
                useStore.getState().actions.modalActions.openAuthModal?.('signup', 'ordinal_thread')
                return
            }

            try {
                ensuredThread = await ensureOrdinalTrackAnswer(track)
                answerId = ensuredThread?.answer_id || null
            } catch {
                return
            }
        }

        logMediaEvent('MEDIA_DOCK_SHOW_IN_THREAD', {
            media_key: current.nowPlayingMediaKey,
            answer_id: answerId || null,
            quest_id: track?.questId || null,
            bsv_ordinal_id: track?.artifactOrdinalId || null,
        })
        const filter = ensuredThread?.child_quest_id
            ? { questId: ensuredThread.child_quest_id, answerId }
            : await resolveMediaDockChildThreadFilter({
                  answerId,
                  getCachedAnswer,
                  getCommentQuest: api.getCommentQuest,
              })
        if (!filter) return

        openCenter(filter)
    }, [ensureOrdinalTrackAnswer])

    // The expanded dock's now-playing tile is one big pointer to the song's
    // thread. Its controls stop their own clicks; the one click that can still
    // arrive by accident is the one a seek drag releases off the slider.
    const stageSeekDragEndedAtRef = useRef(0)
    const handleStageSeekDragging = useCallback(dragging => {
        if (!dragging) stageSeekDragEndedAtRef.current = Date.now()
        setIsDraggingSeek(dragging)
    }, [])
    const handleStageClick = useCallback(() => {
        if (Date.now() - stageSeekDragEndedAtRef.current < STAGE_CLICK_AFTER_DRAG_GRACE_MS) {
            return
        }
        // In cinema the artwork is the screen, not a link: a click there
        // plays or pauses, as it does on the video surface.
        if (isCinemaRef.current) {
            handleTogglePlayback('surface')
            return
        }
        handleShowInThread()
    }, [handleShowInThread, handleTogglePlayback])
    // The thread opens underneath the stage, so cinema ends first.
    const handleStageThreadChipClick = useCallback(
        event => {
            event.stopPropagation()
            exitCinemaRef.current('thread')
            handleShowInThread()
        },
        [handleShowInThread],
    )

    // --- YouTube-style overlay (collapsed video) ---------------------------

    const clearOverlayTimer = useCallback(() => {
        if (overlayTimerRef.current) {
            window.clearTimeout(overlayTimerRef.current)
            overlayTimerRef.current = null
        }
    }, [])

    const pokeOverlay = useCallback(() => {
        setOverlayVisible(true)
        clearOverlayTimer()
        overlayTimerRef.current = window.setTimeout(() => setOverlayVisible(false), OVERLAY_HIDE_MS)
    }, [clearOverlayTimer])

    useEffect(() => clearOverlayTimer, [clearOverlayTimer])

    // --- Cinema chrome ------------------------------------------------------

    const clearCinemaChromeTimer = useCallback(() => {
        if (cinemaChromeTimerRef.current) {
            window.clearTimeout(cinemaChromeTimerRef.current)
            cinemaChromeTimerRef.current = null
        }
    }, [])

    // Pointer intent shows the chrome; it fades again after a moment of
    // stillness unless the pointer is resting on a control. A paused stage
    // keeps it up regardless (see showCinemaChrome).
    const revealCinemaChrome = useCallback(() => {
        setCinemaChromeVisible(true)
        clearCinemaChromeTimer()
        if (cinemaChromePinsRef.current.size) return
        cinemaChromeTimerRef.current = window.setTimeout(() => {
            cinemaChromeTimerRef.current = null
            if (cinemaChromePinsRef.current.size) return
            setCinemaChromeVisible(false)
        }, CINEMA_CHROME_HIDE_MS)
    }, [clearCinemaChromeTimer])

    const pinCinemaChrome = useCallback(
        reason => {
            cinemaChromePinsRef.current.add(reason)
            clearCinemaChromeTimer()
            setCinemaChromeVisible(true)
        },
        [clearCinemaChromeTimer],
    )

    const unpinCinemaChrome = useCallback(
        reason => {
            cinemaChromePinsRef.current.delete(reason)
            revealCinemaChrome()
        },
        [revealCinemaChrome],
    )

    const handleCinemaControlMouseEnter = useCallback(
        () => pinCinemaChrome('hover'),
        [pinCinemaChrome],
    )
    const handleCinemaControlMouseLeave = useCallback(
        () => unpinCinemaChrome('hover'),
        [unpinCinemaChrome],
    )

    // Touch has no hover: a tap on the picture shows or hides the chrome.
    const toggleCinemaChrome = useCallback(() => {
        if (cinemaChromeVisible) {
            clearCinemaChromeTimer()
            setCinemaChromeVisible(false)
        } else {
            revealCinemaChrome()
        }
    }, [cinemaChromeVisible, clearCinemaChromeTimer, revealCinemaChrome])

    const handleCinemaMenuOpenChange = useCallback(
        open => {
            if (!isCinemaRef.current) return
            if (open) pinCinemaChrome('menu')
            else unpinCinemaChrome('menu')
        },
        [pinCinemaChrome, unpinCinemaChrome],
    )

    useEffect(() => clearCinemaChromeTimer, [clearCinemaChromeTimer])

    useEffect(() => {
        if (!isCinema) {
            clearCinemaChromeTimer()
            cinemaChromePinsRef.current.clear()
            setCinemaChromeVisible(true)
            return
        }
        revealCinemaChrome()
    }, [clearCinemaChromeTimer, isCinema, revealCinemaChrome, snapshot.isPlaying])

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return undefined

        const mediaQuery = window.matchMedia(HOVER_VIDEO_CONTROLS_QUERY)
        const syncHoverOverlayMode = () => {
            setUsesHoverVideoOverlay(mediaQuery.matches)
            setIsVideoSurfaceActive(false)
        }

        syncHoverOverlayMode()
        mediaQuery.addEventListener?.('change', syncHoverOverlayMode)
        return () => mediaQuery.removeEventListener?.('change', syncHoverOverlayMode)
    }, [])

    // --- Upvalue ------------------------------------------------------------

    const { formatSatsAmount } = useBsvDisplay()
    // The playing answer's text content, used as the dock title.
    const [answerTitle, setAnswerTitle] = useState(null)
    const [answerAuthor, setAnswerAuthor] = useState(null)
    // The playing post's owner, for the rows only the owner gets (Download).
    const [answerOwnerId, setAnswerOwnerId] = useState(null)
    // Whether the now-playing post is in Music (answers.is_music === true).
    // Read live from the answer cache (the post's own media menu writes it too), so
    // it can never describe a different song than the one playing.
    const nowPlayingCachedAnswer = useCachedAnswer(
        snapshot.nowPlayingTrack?.answerId ? String(snapshot.nowPlayingTrack.answerId) : null,
    )
    const answerInMusic = nowPlayingCachedAnswer?.is_music === true
    const [trackAnswerTitles, setTrackAnswerTitles] = useState({})
    const [trackAnswerArtwork, setTrackAnswerArtwork] = useState({})

    // The tile's top-right dropdown is the media card's: the track's playlist
    // membership and its file, the same rows an audio ordinal card offers.
    const stageMenuActions = useMemo(() => {
        const stageTrack = snapshot.nowPlayingTrack
        const actions = []
        if (!stageTrack?.mediaKey) return actions

        // Video fills the window on request, the same row the post's dropdown
        // offers. (Inside cinema the dropdown is not drawn; the corner toggle
        // is the way back.)
        if (cinemaFeatureEnabled && isVideoLikeTrack(stageTrack) && !isCinema) {
            actions.push({
                label: 'Cinema',
                icon: <TvMinimalPlayIcon size={13} />,
                onClick: (event, { triggerElement }) => {
                    event.preventDefault()
                    event.stopPropagation()
                    openCinemaFromRequestRef.current?.({
                        mediaKey: stageTrack.mediaKey,
                        source: 'stage_menu',
                        returnFocusEl: triggerElement || null,
                        returnFocusSelector:
                            '[data-testid="media-dock-stage-menu"] button[aria-haspopup="menu"]',
                    })
                },
            })
        }
        const inPlaylist = (snapshot.playlist || []).some(
            item => item?.mediaKey === stageTrack.mediaKey,
        )
        actions.push({
            label: inPlaylist ? 'Remove from playlist' : 'Add to playlist',
            icon: inPlaylist ? <Check size={13} /> : <ListMusic size={13} />,
            onClick: event => {
                event.preventDefault()
                event.stopPropagation()
                handleTogglePlaylistMembership('dock_expanded')
            },
        })
        // The post's owner (or a knov_admin) adds it to Music or removes it, the
        // same row the post's own media dropdown offers. Only for a post's own media.
        const viewerOwnsAnswer =
            Boolean(answerOwnerId) && String(answerOwnerId) === String(window.gon?.currentUser?.id)
        if (
            stageTrack.answerId &&
            !stageTrack.artifactOrdinalId &&
            (stageTrack.kind === 'audio' || stageTrack.kind === 'video') &&
            (viewerOwnsAnswer || perms.amKnovAdmin())
        ) {
            const answerId = String(stageTrack.answerId)
            actions.push({
                label: answerInMusic ? 'Remove from Music' : 'Add to Music',
                icon: <Music size={13} />,
                onClick: async event => {
                    event.preventDefault()
                    event.stopPropagation()
                    try {
                        const result = await api.setAnswerIsMusic(answerId, !answerInMusic)
                        const base = getCachedAnswer(answerId) || {}
                        cacheAnswer({
                            ...base,
                            ...(result?.answer || {}),
                            is_music: !answerInMusic,
                        })
                        toast.success(answerInMusic ? 'Removed from Music' : 'Added to Music')
                    } catch (error) {
                        console.error('Failed to update music flag:', error)
                        toast.error("Couldn't update the music flag.")
                    }
                },
            })
        }
        // The file leaves the app by its owner's hand: an artifact's holder,
        // or the post's author. A knov_admin gets a video's row as well.
        const trackOwnerId = stageTrack.artifactOrdinalId
            ? stageTrack.artifactOwnerId
            : answerOwnerId
        const viewerOwnsTrack =
            Boolean(trackOwnerId) && String(trackOwnerId) === String(window.gon?.currentUser?.id)
        const viewerMayDownload =
            viewerOwnsTrack || (stageTrack.kind === 'video' && perms.amKnovAdmin())
        if (
            stageTrack.src &&
            (stageTrack.kind === 'audio' || stageTrack.kind === 'video') &&
            viewerMayDownload
        ) {
            actions.push({
                label: 'Download',
                icon: <Download size={13} />,
                href: stageTrack.src,
                download: true,
                downloadName: answerTitle || stageTrack.title || 'media',
            })
        }
        return actions
    }, [
        snapshot.nowPlayingTrack,
        snapshot.playlist,
        answerOwnerId,
        answerInMusic,
        answerTitle,
        cinemaFeatureEnabled,
        handleTogglePlaylistMembership,
        isCinema,
    ])

    useEffect(() => {
        const answerId = snapshot.nowPlayingTrack?.answerId
        if (!answerId) {
            setAnswerTitle(null)
            setAnswerAuthor(null)
            setAnswerOwnerId(null)
            return undefined
        }

        let cancelled = false
        const resolve = answer => {
            if (cancelled || !answer) return false
            setAnswerTitle(deriveMediaDockAnswerTitle(answer))
            setAnswerAuthor(answer.user || null)
            setAnswerOwnerId(answer.user_id ?? answer.user?.id ?? null)
            cacheAnswer(answer)
            const artwork = deriveMediaDockAnswerArtwork(answer)
            setTrackAnswerArtwork(previous =>
                previous[answerId] === artwork ? previous : { ...previous, [answerId]: artwork },
            )
            return true
        }

        setAnswerTitle(null)
        setAnswerAuthor(null)
        setAnswerOwnerId(null)
        if (resolve(getCachedAnswer(String(answerId)))) return undefined

        api.getAnswer(answerId)
            .then(answer => {
                resolve(answer)
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [snapshot.nowPlayingTrack?.answerId])

    useEffect(() => {
        const answerIds = Array.from(
            new Set(
                [...snapshot.playlist, ...snapshot.history, ...radioPool.tracks]
                    .map(item => item?.answerId)
                    .filter(Boolean)
                    .map(String),
            ),
        )

        if (answerIds.length === 0) return undefined

        let cancelled = false
        const cachedTitles = {}
        const cachedArtwork = {}
        const missingIds = []

        answerIds.forEach(answerId => {
            const cachedAnswer = getCachedAnswer(answerId)
            const cachedTitle = deriveMediaDockAnswerTitle(cachedAnswer)
            const cachedAnswerArtwork = deriveMediaDockAnswerArtwork(cachedAnswer)
            if (cachedTitle) {
                cachedTitles[answerId] = cachedTitle
            }
            if (cachedAnswerArtwork) {
                cachedArtwork[answerId] = cachedAnswerArtwork
            }
            if (
                !cachedAnswer &&
                (!hasOwn(trackAnswerTitles, answerId) || !hasOwn(trackAnswerArtwork, answerId))
            ) {
                missingIds.push(answerId)
            }
        })

        if (Object.keys(cachedTitles).length > 0) {
            setTrackAnswerTitles(previous => {
                let changed = false
                const next = { ...previous }
                Object.entries(cachedTitles).forEach(([answerId, title]) => {
                    if (next[answerId] !== title) {
                        next[answerId] = title
                        changed = true
                    }
                })
                return changed ? next : previous
            })
        }

        if (Object.keys(cachedArtwork).length > 0) {
            setTrackAnswerArtwork(previous => {
                let changed = false
                const next = { ...previous }
                Object.entries(cachedArtwork).forEach(([answerId, artwork]) => {
                    if (next[answerId] !== artwork) {
                        next[answerId] = artwork
                        changed = true
                    }
                })
                return changed ? next : previous
            })
        }

        if (missingIds.length === 0) return undefined

        api.getAnswers(missingIds)
            .then(answers => {
                if (cancelled) return

                const fetchedTitles = {}
                const fetchedArtwork = {}
                ;(answers || []).forEach(answer => {
                    if (!answer?.id) return
                    fetchedTitles[String(answer.id)] = deriveMediaDockAnswerTitle(answer)
                    fetchedArtwork[String(answer.id)] = deriveMediaDockAnswerArtwork(answer)
                })

                setTrackAnswerTitles(previous => {
                    let changed = false
                    const next = { ...previous }
                    missingIds.forEach(answerId => {
                        const title = fetchedTitles[answerId] || null
                        if (next[answerId] !== title) {
                            next[answerId] = title
                            changed = true
                        }
                    })
                    return changed ? next : previous
                })
                setTrackAnswerArtwork(previous => {
                    let changed = false
                    const next = { ...previous }
                    missingIds.forEach(answerId => {
                        const artwork = fetchedArtwork[answerId] || null
                        if (next[answerId] !== artwork) {
                            next[answerId] = artwork
                            changed = true
                        }
                    })
                    return changed ? next : previous
                })
            })
            .catch(() => {})

        return () => {
            cancelled = true
        }
    }, [
        snapshot.playlist,
        snapshot.history,
        radioPool.tracks,
        trackAnswerArtwork,
        trackAnswerTitles,
    ])

    // The quick upvalue's target, looked up on each tap: the playing track's
    // answer, threading the ordinal first when an artifact has none yet.
    const resolveUpvalueTarget = useCallback(async () => {
        const current = getMediaDockSnapshot()
        const track = current.nowPlayingTrack
        const artifactOrdinalId = track?.artifactOrdinalId || null
        let answerId = track?.answerId
        if (!answerId && !artifactOrdinalId) return null

        let answer = answerId ? getCachedAnswer(String(answerId)) : null
        if (!answerId && artifactOrdinalId) {
            try {
                const result = await ensureOrdinalTrackAnswer(track)
                answerId = result?.answer_id || null
                answer = result?.answer || null
            } catch {
                answer = null
            }
        }
        if (!answerId) return null

        logMediaEvent('MEDIA_DOCK_UPVALUE', {
            media_key: current.nowPlayingMediaKey,
            answer_id: answerId,
            bsv_ordinal_id: artifactOrdinalId,
        })

        if (!answer) {
            try {
                answer = await api.getAnswer(answerId)
            } catch {
                answer = null
            }
        }
        if (!answer) return null

        return {
            answer,
            artifactOrdinalId,
            artifactOwnerId: artifactOrdinalId ? track?.artifactOwnerId : null,
        }
    }, [ensureOrdinalTrackAnswer])

    const track = snapshot.nowPlayingTrack
    const stageAuthorHandle = answerAuthor?.username || answerAuthor?.name || null
    const displayArtwork = getMediaDockTrackDisplayArtwork(track, {
        answerArtwork: trackAnswerArtwork[track?.answerId],
        getAnswerById: getCachedAnswer,
    })
    const history = snapshot.history
    const playlist = snapshot.playlist
    const historyPagination = snapshot.pagination.history
    const playlistPagination = snapshot.pagination.playlist
    const historyCount = Math.max(history.length, historyPagination.totalCount)
    const playlistCount = Math.max(playlist.length, playlistPagination.totalCount)
    const isPlaying = snapshot.isPlaying
    // The tab the sound is coming from: the station while radio is on, else
    // the playlist when the song is in it, else history (every play lands there).
    const playingTab =
        !isPlaying || !track
            ? null
            : radioEnabled
            ? 'radio'
            : playlist.some(item => item.mediaKey === track.mediaKey)
            ? 'playlist'
            : 'history'
    const renderTabPlayingIcon = tab =>
        playingTab === tab ? (
            // Decorative: an accessible name here would rename the tab
            // ("Radio Playing").
            <Play className={styles.tabPlayingIcon} aria-hidden="true">
                <title>Playing</title>
            </Play>
        ) : null
    const canResolveOrdinalAnswer = Boolean(track?.artifactOrdinalId && bsvOrdinalControlsEnabled())
    const hasAnswerControls = Boolean(track?.answerId || canResolveOrdinalAnswer)
    const displayTitle = track
        ? answerTitle || track.title || 'Untitled'
        : activeTab === 'history'
        ? 'History'
        : activeTab === 'radio'
        ? 'Treechat Radio'
        : 'Playlist'

    const takeoverTrack = useMemo(
        () => (takeover ? getMediaTrack(takeover.mediaKey) : null),
        [takeover],
    )
    const deferInitialOwnerPause = isDeferredDockTakeover({
        takeover,
        handoff: snapshot.handoff,
    })

    // Previous walks play history. Next walks the playlist, or the shuffled
    // playlist when shuffle is active. The frozen navQueue lets repeated
    // previous presses continue backward even though claims reorder history.
    const { nextTrack, previousTrack } = useMemo(() => {
        return {
            nextTrack: getNextPlaylistTrack(playlist, nowPlayingKey, {
                loop: loopEnabled,
                shuffle: shuffleEnabled,
                playedKeys: shufflePlayedKeysRef.current,
            }).track,
            previousTrack: getPreviousHistoryTrack(history, nowPlayingKey, {
                navQueue,
                getTrack: getMediaTrack,
            }),
        }
    }, [playlist, history, nowPlayingKey, navQueue, loopEnabled, shuffleEnabled])

    // Step within the frozen queue (capturing it on the first step).
    const handleNavStep = useCallback(
        direction => {
            // Live radio: "next" is a resync to whatever is on the air;
            // stepping back into history is leaving the broadcast.
            if (direction === 'next' && playbackModesRef.current.radio) {
                setNavQueue(null)
                syncRadioLive('radio_next')
                return
            }
            if (direction === 'prev' && playbackModesRef.current.radio) {
                stopRadio('radio_prev')
            }

            const target =
                direction === 'next' ? selectNextPlaylistTrack(nowPlayingKey) : previousTrack
            if (!target) return

            if (direction === 'prev' && (!navQueue || !navQueue.includes(nowPlayingKey))) {
                setNavQueue(history.map(item => item.mediaKey))
            }
            if (direction === 'next') {
                setNavQueue(null)
            }
            navigatingRef.current = true
            playTrack(target, direction)
        },
        [
            previousTrack,
            history,
            navQueue,
            nowPlayingKey,
            playTrack,
            selectNextPlaylistTrack,
            stopRadio,
            syncRadioLive,
        ],
    )

    const handleHistoryChanged = useCallback(() => {
        setSnapshot(getMediaDockSnapshot())
    }, [])

    // With radio on, next re-syncs to the live broadcast, so the button only
    // dims once the rotation is known to be empty.
    const hasRadioNext = radioEnabled && radioPool.status !== 'empty'
    const canStepNext = Boolean(nextTrack) || hasRadioNext

    // The on-air song's winning odds, while the dock is playing the live
    // broadcast (not a song picked from the list).
    const liveRadioNow = radioLive?.now
    const liveRadioOdds =
        radioEnabled &&
        track &&
        liveRadioNow?.chance != null &&
        liveRadioNow.track?.mediaKey === track.mediaKey
            ? {
                  chance: liveRadioNow.chance,
                  isDiscovery: liveRadioNow.weightSource === 'discovery',
                  blockHeight: liveRadioNow.blockHeight,
              }
            : null
    const radioUpNextTrack = radioLive?.next?.track || null
    const radioUpNextTitle = radioUpNextTrack
        ? (radioUpNextTrack.answerId && trackAnswerTitles[String(radioUpNextTrack.answerId)]) ||
          getMediaDockTrackDisplayTitle(radioUpNextTrack, {
              getAnswerById: answerId => getCachedAnswer(answerId),
          }) ||
          radioUpNextTrack.title
        : null

    // The dock stays out of the way while the inline player is on screen. A
    // plain offscreen measurement can be stale during track changes, so the
    // dock only appears after an explicit adoption or after it has already
    // launched.
    const isPlaylistPanelOpen =
        !track && isExpanded && ['playlist', 'history', 'radio'].includes(activeTab) && isLaunched
    const isVisible =
        !isClosed && (Boolean(track) ? Boolean(takeover) || isLaunched : isPlaylistPanelOpen)
    // Hosted playback never warms a second player: the dock's runtime IS the
    // player, so there is nothing to preload for.
    const shouldPreloadDockYouTube =
        !hostedPlaybackEnabled &&
        !isVisible &&
        !takeoverTrack &&
        !isClosed &&
        isPlaying &&
        track?.kind === 'youtube' &&
        Boolean(track.mediaKey)
    const dockMediaTrack = takeoverTrack || (shouldPreloadDockYouTube ? track : null)
    const isWaitingForDockYouTubePreload = shouldWaitForDockYouTubePreload({
        track: takeoverTrack,
        deferInitialOwnerPause,
        readyMediaKey: youtubePreloadReadyMediaKey,
    })
    const isDockPreloading = Boolean(
        dockMediaTrack && (shouldPreloadDockYouTube || isWaitingForDockYouTubePreload),
    )
    const isPresented = isVisible && !isDockPreloading
    isPresentedRef.current = isPresented
    dockHiddenPreparingRef.current = isWaitingForDockYouTubePreload
    const isHostedAttached = Boolean(
        hostedPlacement && takeoverTrack && hostedPlacement.mediaKey === takeoverTrack.mediaKey,
    )
    const presentedMediaKey = isPresented ? dockMediaTrack?.mediaKey || null : null
    useLayoutEffect(() => {
        setMediaDockPresentedMediaKey(presentedMediaKey)
        return () => setMediaDockPresentedMediaKey(null)
    }, [presentedMediaKey])

    // Parked over the post's media frame (YouTube, video).
    const isHostedInline = isHostedAttached && hostedPlacement.kind !== 'silent'
    // Owned by the dock but with nothing to show (audio): the post keeps
    // drawing its own transport, so the dock stays out of sight until the post
    // leaves the screen.
    const isHostedSilent = isHostedAttached && hostedPlacement.kind === 'silent'

    // The runtime the placement belongs to went away or changed track: the
    // placement is stale. Nothing to morph — the corner dock (if any) is
    // already the current presentation.
    useEffect(() => {
        const placement = hostedPlacementRef.current
        if (!placement) return
        if (!takeoverMediaKey) {
            leaveHostedPlacement('runtime-released', { keepRuntime: true })
        } else if (takeoverMediaKey !== placement.mediaKey) {
            leaveHostedPlacement('track-changed', { keepRuntime: true })
        }
    }, [takeoverMediaKey, leaveHostedPlacement])

    // Inline surfaces target the actual live surface for fullscreen while it
    // is moved into the post; fallback placement still uses the dock root.
    useLayoutEffect(() => {
        const surfaceElement =
            hostedPlacement?.positionMode === 'flow' ? mediaSurfaceRef.current : rootRef.current
        setMediaDockHostedSurfaceElement(isPresented ? surfaceElement : null)
        return () => setMediaDockHostedSurfaceElement(null)
    }, [hostedPlacement?.positionMode, isPresented])

    // Per-frame origin tracking while hosted: follow the frame through
    // scrolls and panel slides, clip to what the user can see, and glide to
    // the corner the moment the frame is fully out of view or unmounts.
    //
    // The loop has to run per frame — a panel slide moves the frame through an
    // ancestor transform, which no scroll or intersection event reports — so it
    // is built to cost almost nothing when nothing moves: a still frame is one
    // `getBoundingClientRect` and zero style writes (see the placement cache in
    // the coordinator and the skip-unchanged writes in applyHostedPlacementStyle).
    useEffect(() => {
        if (!hostedPlacement || typeof window === 'undefined') return undefined

        // A silent placement pins nothing, so it does not need frame-accurate
        // tracking — it only has to notice the post leaving the screen. Poll it
        // at the dock's own cadence instead of every frame.
        const isSilent = hostedPlacement.kind === 'silent'
        let frame = null
        let timer = null
        let active = true
        const placementCache = createMediaTrackPlacementCache()
        let appliedStyle = null
        let appliedResidual = { x: 0, y: 0 }
        // The frame's curve can change while it is hosted (the card reflows
        // around it, the window resizes), so it is re-read, but at the silent
        // poll's cadence rather than every frame: it walks the ancestors.
        let cornerRadii = hostedGeometryRef.current?.cornerRadii ?? null
        let cornerRadiiReadAt = performance.now()
        const refreshCornerRadii = (current, placement) => {
            const now = performance.now()
            if (now - cornerRadiiReadAt < HOSTED_SILENT_POLL_MS) return false
            cornerRadiiReadAt = now
            const next = resolveHostedCornerRadii(current.originEl, placement.rect)
            if (sameHostedCornerRadii(next, cornerRadii)) return false
            cornerRadii = next
            if (current.positionMode === 'flow') {
                mediaSurfaceRef.current?.style.setProperty(
                    '--hosted-frame-clip',
                    buildHostedFlowClipPath(cornerRadii),
                )
            }
            return true
        }
        const schedule = () => {
            if (isSilent) timer = window.setTimeout(tick, HOSTED_SILENT_POLL_MS)
            else frame = window.requestAnimationFrame(tick)
        }
        const tick = () => {
            frame = null
            timer = null
            if (!active) return
            const current = hostedPlacementRef.current
            if (!current) return

            const placement = getMediaTrackOriginPlacement(current.mediaKey, {
                originEl: current.originEl,
                cache: placementCache,
            })
            const surfaceFullscreen =
                isHostedSurfaceFullscreen(rootRef.current) ||
                isHostedSurfaceFullscreen(mediaSurfaceRef.current)
            if (
                !shouldKeepHostedPlacement({
                    placement,
                    originEl: current.originEl,
                    surfaceFullscreen,
                })
            ) {
                leaveHostedPlacement(
                    resolveHostedDetachReason({ placement, originEl: current.originEl }) ||
                        'origin-offscreen',
                )
                return
            }
            // Fullscreen CSS owns the geometry, and the frame behind it may
            // have no rect to measure; resume tracking once it ends.
            if (surfaceFullscreen) {
                schedule()
                return
            }

            // In-flow: the live surface is a child of the origin and therefore
            // inherits scrolling, clipping, and panel transforms. The loop only
            // watches for detach; it never writes visual geometry.
            const radiiChanged = !isSilent && refreshCornerRadii(current, placement)

            if (!isSilent && current.positionMode === 'flow') {
                hostedGeometryRef.current = { rect: placement.rect, clip: null, cornerRadii }
                schedule()
                return
            }

            // Anchored: the engine already put the dock on the frame, so the
            // only JS left is correcting an ancestor transform (a panel slide,
            // which anchor positioning does not see) and the clip.
            if (!isSilent && current.positionMode === 'anchored') {
                const rootEl = rootRef.current
                const clip = computeHostedClipInset(placement.rect, placement.bounds)
                hostedGeometryRef.current = { rect: placement.rect, clip, cornerRadii }
                if (rootEl && !isHostedSurfaceFullscreen(rootEl)) {
                    // The measured rect already includes the residual we last
                    // applied, so the correction accumulates rather than
                    // replaces — otherwise it oscillates by its own value.
                    const delta = computeAnchorResidual(
                        placement.rect,
                        rootEl.getBoundingClientRect(),
                    )
                    const residual = {
                        x: appliedResidual.x + delta.x,
                        y: appliedResidual.y + delta.y,
                    }
                    if (delta.x || delta.y) {
                        incrementMediaDockQaCounter('hosted.anchorCorrections')
                    }
                    appliedResidual = residual
                    appliedStyle =
                        applyHostedAnchoredStyle(rootEl, residual, clip, {
                            previous: appliedStyle,
                            borderRadius: cornerRadii,
                        }) || appliedStyle
                }
                schedule()
                return
            }

            // The frame has not moved, its bounds are still fresh and its curve
            // has not changed: the style on the element is already correct, so
            // touch nothing.
            if (!isSilent && (!placement.unchanged || radiiChanged)) {
                const clip = computeHostedClipInset(placement.rect, placement.bounds)
                hostedGeometryRef.current = { rect: placement.rect, clip, cornerRadii }
                const rootEl = rootRef.current
                if (rootEl && !isHostedSurfaceFullscreen(rootEl)) {
                    appliedStyle =
                        applyHostedPlacementStyle(rootEl, placement.rect, clip, {
                            previous: appliedStyle,
                            borderRadius: cornerRadii,
                        }) || appliedStyle
                }
            }
            schedule()
        }

        schedule()
        return () => {
            active = false
            if (frame) window.cancelAnimationFrame(frame)
            if (timer) window.clearTimeout(timer)
        }
    }, [hostedPlacement, leaveHostedPlacement])

    const handleYouTubePreloadStateChange = useCallback((mediaKey, isReady) => {
        setYoutubePreloadReadyMediaKey(previousMediaKey => {
            if (isReady) return mediaKey
            return previousMediaKey === mediaKey ? null : previousMediaKey
        })
    }, [])

    useEffect(() => {
        if (!isDockPreloading) return undefined
        if (typeof window === 'undefined') return undefined

        const interval = window.setInterval(() => {
            setYoutubePreloadSyncToken(token => token + 1)
        }, DOCK_YOUTUBE_PRELOAD_SYNC_INTERVAL_MS)
        return () => window.clearInterval(interval)
    }, [dockMediaTrack?.mediaKey, isDockPreloading])

    // Entrance transition: morph the dock itself — already showing the live
    // video — from the inline player's last on-screen rect into its corner
    // spot (FLIP). With no rect to start from, pop in with the landing
    // animation instead.
    const runCornerMorph = useCallback(
        (startRectOverride = null) => {
            setIsLaunched(true)
            // Cinema fills the window: there is no corner to morph into, and
            // the placement offsets written below would pin the root to it.
            if (isCinemaRef.current) return

            const dockEl = rootRef.current

            // Apply the New-button offset before measuring the morph target so
            // the dock animates straight to its final spot.
            const dockPlacement = syncDockPlacement({ force: true })
            // A dock the user dragged stands at its own top/left with
            // bottom/right released, and React will not rewrite those
            // unchanged values: writing the corner offsets here would leave it
            // pinned at both top and bottom, a box stretched around the player
            // that grows as it is dragged.
            const draggedPosition =
                window.innerWidth <= 900 && !latestRef.current.isExpanded
                    ? mobileDockPositionRef.current
                    : null
            if (dockEl && draggedPosition) {
                dockEl.style.bottom = 'auto'
                dockEl.style.right = 'auto'
            } else {
                if (dockEl && dockPlacement?.bottom) {
                    dockEl.style.bottom = `${dockPlacement.bottom}px`
                }
                if (dockEl && window.innerWidth <= 900 && dockPlacement?.right) {
                    dockEl.style.right = `${dockPlacement.right}px`
                }
            }
            // The corner's bottom offset transitions (so the dock follows the
            // phone navbar sliding away and back). Landing here must not: the morph
            // measures its target right below, and mid-transition that is the
            // old spot, so the dock lands there and then drifts.
            finishDockOffsetTransitions(dockEl)

            // Prefer the origin's last on-screen rect — where the user actually
            // saw the video — over its current (usually already offscreen) rect.
            const capturedRect =
                originRectRef.current?.mediaKey === nowPlayingKey
                    ? originRectRef.current.rect
                    : null
            const startRect = prefersReducedMotion()
                ? null
                : startRectOverride || capturedRect || getMediaTrackOriginRect(nowPlayingKey)

            // A runtime arriving from a hosted placement carries that frame's
            // width; measure the settled corner size, not a width transition
            // in flight.
            if (dockEl && startRectOverride) {
                dockEl.style.transition = 'none'
            }
            const targetRect = dockEl?.getBoundingClientRect()

            if (!startRect || !targetRect?.width || !dockEl?.animate) {
                if (dockEl) dockEl.style.transition = ''
                setIsLanding(true)
                return
            }

            const translateX = startRect.left - targetRect.left
            const translateY = startRect.top - targetRect.top
            const scaleX = startRect.width / targetRect.width
            const scaleY = startRect.height / targetRect.height

            try {
                const animation = dockEl.animate(
                    [
                        {
                            transformOrigin: 'top left',
                            transform: `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`,
                            opacity: 0.9,
                            borderRadius: '10px',
                        },
                        {
                            transformOrigin: 'top left',
                            transform: 'translate(0px, 0px) scale(1, 1)',
                            opacity: 1,
                            borderRadius: '16px',
                        },
                    ],
                    {
                        id: HOSTED_CORNER_MORPH_ID,
                        duration: MORPH_DURATION_MS,
                        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                    },
                )
                if (startRectOverride && animation) {
                    const restoreTransition = () => {
                        dockEl.style.transition = ''
                    }
                    animation.onfinish = restoreTransition
                    animation.oncancel = restoreTransition
                }
            } catch {
                if (dockEl) dockEl.style.transition = ''
                setIsLanding(true)
            }
        },
        [nowPlayingKey, syncDockPlacement],
    )

    const wasVisibleRef = useRef(false)
    useLayoutEffect(() => {
        const wasVisible = wasVisibleRef.current
        wasVisibleRef.current = isPresented
        if (!isPresented || wasVisible) return

        // Presented over its origin frame: there is no corner to enter yet.
        if (hostedPlacementRef.current) {
            setIsLaunched(true)
            return
        }

        runCornerMorph()
    }, [isPresented, runCornerMorph])

    // A playing hosted runtime left its origin: the SAME element glides from
    // where the user last saw it to the corner.
    useLayoutEffect(() => {
        if (isHostedInline) return
        const pending = pendingCornerMorphRef.current
        if (!pending) return
        pendingCornerMorphRef.current = null
        if (!isPresented) return
        runCornerMorph(pending.rect)
    }, [isHostedInline, isPresented, runCornerMorph])

    useEffect(() => {
        if (!isPresented) return undefined

        const handleResize = () => {
            syncDockPlacement({ force: true })
            const currentPosition = mobileDockPositionRef.current
            if (currentPosition && window.innerWidth <= 900 && !isExpanded) {
                updateMobileDockPosition(
                    constrainDockPosition(currentPosition.left, currentPosition.top),
                )
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [
        constrainDockPosition,
        isExpanded,
        isPresented,
        syncDockPlacement,
        updateMobileDockPosition,
    ])

    if (!isVisible && !isDockPreloading) return null

    const isVideoTakeover = isVideoLikeTrack(dockMediaTrack)
    // Collapsed video mode applies to any video track, dock-owned or not —
    // without a dock element the surface shows the poster frame instead.
    const isVideoMode = isVideoLikeTrack(track)
    // The small dock is one card for every track: video shows its picture,
    // audio its combined-media artwork, and both carry the same tap/hover
    // controls.
    const isCompactCard = !isExpanded && Boolean(track)
    const isCompactAudio = isCompactCard && !isVideoMode
    const showVideoSurface = isVideoTakeover || isCompactCard
    // A paused or scrubbed stage keeps its chrome; a playing one lets it fade.
    const showCinemaChrome = !isPlaying || isDraggingSeek || cinemaChromeVisible
    const showVideoOverlay =
        isCompactCard &&
        (usesHoverVideoOverlay ? isVideoSurfaceActive : overlayVisible || !isPlaying)
    const handleSurfaceClick = () => {
        if ((!isVideoMode && !isCompactCard) || isHostedInline) return
        if (isExpanded) {
            // Cinema on touch: the tap shows or hides the chrome; the transport's
            // play control drives playback. With a pointer, the picture itself
            // is the play/pause control, as on the expanded tile.
            if (isCinema && !usesHoverVideoOverlay) {
                toggleCinemaChrome()
                return
            }
            handleTogglePlayback('surface')
            return
        }
        if (usesHoverVideoOverlay) {
            handleTogglePlayback('surface')
            return
        }
        if (overlayVisible) {
            clearOverlayTimer()
            setOverlayVisible(false)
        } else {
            pokeOverlay()
        }
    }

    const handleSurfaceMouseEnter = () => {
        if (!usesHoverVideoOverlay) return
        clearOverlayTimer()
        setIsVideoSurfaceActive(true)
    }

    const handleSurfaceMouseLeave = event => {
        if (!usesHoverVideoOverlay) return
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null
        if (
            typeof Node !== 'undefined' &&
            activeElement instanceof Node &&
            event.currentTarget.contains(activeElement)
        ) {
            return
        }
        setIsVideoSurfaceActive(false)
    }

    const handleSurfaceFocus = () => {
        if (isCinemaRef.current) {
            pinCinemaChrome('focus')
            return
        }
        if (!usesHoverVideoOverlay) return
        clearOverlayTimer()
        setIsVideoSurfaceActive(true)
    }

    const handleSurfaceBlur = event => {
        if (isCinemaRef.current) {
            if (
                typeof Node !== 'undefined' &&
                event.relatedTarget instanceof Node &&
                event.currentTarget.contains(event.relatedTarget)
            ) {
                return
            }
            unpinCinemaChrome('focus')
            return
        }
        if (!usesHoverVideoOverlay) return
        if (
            typeof Node !== 'undefined' &&
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
        ) {
            return
        }
        setIsVideoSurfaceActive(false)
    }

    const overlayAction = handler => event => {
        event.stopPropagation()
        handler()
        pokeOverlay()
    }

    const playPauseButton = (className, iconClassName, source = 'dock') => (
        <button
            type="button"
            className={className}
            onClick={() => handleTogglePlayback(source)}
            aria-label={
                radioEnabled ? (isPlaying ? 'Tune out' : 'Tune in') : isPlaying ? 'Pause' : 'Play'
            }
        >
            {radioEnabled ? (
                <RadioTower className={iconClassName} aria-hidden="true" />
            ) : isPlaying ? (
                <Pause className={iconClassName} aria-hidden="true" />
            ) : (
                <Play className={cn(iconClassName, styles.playIconOffset)} aria-hidden="true" />
            )}
        </button>
    )

    // The same quick upvalue as the Music header's song card.
    const upvalueControl = hasAnswerControls ? (
        <span className={styles.upvalueControl}>
            <QuickUpvalueButton
                resolveTarget={resolveUpvalueTarget}
                targetKey={nowPlayingKey}
                source="media_dock"
                authSource="media_dock_upvalue"
            />
        </span>
    ) : null

    // Small-player signal: just the neon arrow (green = upvalue, purple =
    // upvote), no pill or amount.
    const signalNeon = hasAnswerControls ? (
        <span className={cn(styles.upvalueControl, styles.stackUpvalueControl)}>
            <QuickUpvalueButton
                resolveTarget={resolveUpvalueTarget}
                targetKey={nowPlayingKey}
                source="media_dock"
                authSource="media_dock_upvalue"
                variant="arrow"
                className={cn(styles.upvoteGlow, styles.upvalueNeonGreen)}
            />
        </span>
    ) : null

    // What put the live song on the air: its odds and how it won the draw.
    const liveRadioOddsBadge = liveRadioOdds ? (
        <span
            className={styles.radioStageOdds}
            title={
                liveRadioOdds.blockHeight
                    ? `Drawn by BSV block #${liveRadioOdds.blockHeight.toLocaleString()}`
                    : undefined
            }
        >
            <span className={styles.radioStageOddsValue}>
                {formatRadioChance(liveRadioOdds.chance)}
            </span>
            <span className={styles.radioStageOddsLabel}>
                {liveRadioOdds.isDiscovery ? 'odds · discovery pick' : 'odds · won the draw'}
            </span>
        </span>
    ) : null

    const canOpenThread = hasAnswerControls
    const nowPlayingInPlaylist = Boolean(
        nowPlayingKey && (playlist || []).some(item => item?.mediaKey === nowPlayingKey),
    )
    const isMuted = volume <= 0.001

    // The expanded tile's controls row ends like the radio card's: the
    // playlist toggle (with its count) and the quick upvalue.
    const nowPlayingPlaylistCount = Number(nowPlayingCachedAnswer?.playlist_count) || 0
    const stageTransportActions =
        !isCinema && track ? (
            <>
                {track.mediaKey && (
                    <button
                        type="button"
                        className={cn(
                            audioStyles.audioPlaylistButton,
                            nowPlayingInPlaylist && audioStyles.audioPlaylistButtonActive,
                        )}
                        onClick={event => {
                            event.stopPropagation()
                            handleTogglePlaylistMembership('dock_stage')
                        }}
                        aria-label={
                            nowPlayingInPlaylist ? 'Remove from playlist' : 'Add to playlist'
                        }
                        aria-pressed={nowPlayingInPlaylist}
                        title={nowPlayingInPlaylist ? 'Remove from playlist' : 'Add to playlist'}
                        data-testid="media-dock-stage-playlist"
                    >
                        {nowPlayingInPlaylist ? (
                            <Check
                                className={audioStyles.audioPlaylistButtonIcon}
                                aria-hidden="true"
                            />
                        ) : (
                            <ListMusic
                                className={audioStyles.audioPlaylistButtonIcon}
                                aria-hidden="true"
                            />
                        )}
                        {nowPlayingPlaylistCount > 0 && <span>{nowPlayingPlaylistCount}</span>}
                    </button>
                )}
                {upvalueControl}
            </>
        ) : null

    // Hover on a control holds the cinema chrome up while the video plays.
    const cinemaChromeHoverProps = isCinema
        ? {
              onMouseEnter: handleCinemaControlMouseEnter,
              onMouseLeave: handleCinemaControlMouseLeave,
          }
        : {}
    // In cinema the transport carries the video player's settings (speed,
    // captions) and fullscreen; the expanded tile keeps to the card's strip.
    const cinemaTransportProps = isCinema
        ? {
              playbackRate: stageRuntimeControls.playbackRate,
              playbackRates: stageRuntimeControls.playbackRates,
              onPlaybackRateChange: handleStagePlaybackRate,
              captionsAvailable: stageRuntimeControls.captionsAvailable,
              captionsEnabled: stageRuntimeControls.captionsEnabled,
              onToggleCaptions: handleStageToggleCaptions,
              // A tap-opened menu holds the chrome up, as hovering a control does.
              onSettingsOpenChange: handleCinemaMenuOpenChange,
              isFullscreen: isCinemaFullscreen,
              onFullscreen: handleStageFullscreen,
          }
        : {}
    // Touch owns show/hide through the completed click. Revealing on its
    // pointerdown would make that same tap observe "visible" and hide again.
    const cinemaPointerIntentProps =
        isCinema && usesHoverVideoOverlay
            ? { onPointerMove: revealCinemaChrome, onPointerDown: revealCinemaChrome }
            : {}

    const renderStageChrome = ({ showThreadHint }) => (
        <>
            <div className={styles.stageScrim} aria-hidden="true" />
            {canOpenThread && (
                // The post's author fronts the thread link: avatar and handle,
                // the way the app names them elsewhere.
                <button
                    type="button"
                    className={styles.stageThreadChip}
                    onClick={handleStageThreadChipClick}
                    aria-label={stageAuthorHandle ? `${stageAuthorHandle} — open thread` : 'Chat'}
                    title={
                        stageAuthorHandle
                            ? `Open the thread with ${stageAuthorHandle}`
                            : 'Chat — open thread'
                    }
                    data-testid="media-dock-stage-chat"
                    {...cinemaChromeHoverProps}
                >
                    {answerAuthor ? (
                        <UserIcon
                            user={answerAuthor}
                            size="small"
                            contextStyles={styles.stageChipAvatar}
                        />
                    ) : (
                        <MessageCircle aria-hidden="true" />
                    )}
                    <span className={styles.stageChipLabel}>{stageAuthorHandle || 'Chat'}</span>
                </button>
            )}
            {canOpenThread && showThreadHint && (
                // Hover affordance for artwork stages, whose full tile is a link.
                <span className={styles.stageHint} aria-hidden="true">
                    <MessageCircle aria-hidden="true" />
                    <span>Open thread</span>
                </span>
            )}
            {isCinema ? (
                // In cinema the corner holds one thing: the expanded dock's
                // collapse control, which takes the picture back to regular.
                // No dropdown here.
                <div
                    className={styles.stageCinemaCorner}
                    onClick={event => event.stopPropagation()}
                    data-testid="media-dock-cinema-corner"
                    {...cinemaChromeHoverProps}
                >
                    <button
                        ref={cinemaExitButtonRef}
                        type="button"
                        className={styles.stageCinemaToggle}
                        onClick={() => exitCinema('button')}
                        aria-label="Exit cinema"
                        title="Exit cinema (Esc)"
                        data-testid="media-dock-cinema-exit"
                    >
                        <ChevronDown aria-hidden="true" />
                    </button>
                </div>
            ) : (
                stageMenuActions.length > 0 && (
                    // The menu portals its list; React still bubbles its clicks
                    // through here, so stop them short of the tile interaction.
                    <div
                        className={styles.stageMenu}
                        onClick={event => event.stopPropagation()}
                        data-testid="media-dock-stage-menu"
                    >
                        <PayloadTileMenu
                            actions={stageMenuActions}
                            kind={isVideoMode ? 'video' : 'audio'}
                            ariaLabel="Media actions"
                        />
                    </div>
                )
            )}
            <div className={styles.stageFooter} {...cinemaChromeHoverProps}>
                <AudioArtworkTitle title={displayTitle} testId="media-dock-stage-title" />
                <MediaDockStageTransport
                    mediaKey={nowPlayingKey}
                    isPlaying={isPlaying}
                    isDocumentVisible={isDocumentVisible}
                    liveRadio={radioEnabled}
                    onTogglePlayback={() => handleTogglePlayback('stage')}
                    onDraggingChange={handleStageSeekDragging}
                    volume={volume}
                    isMuted={isMuted}
                    onVolumeChange={handleVolume}
                    onToggleMute={handleToggleMute}
                    onVolumeMouseLeave={blurContainedActiveElement}
                    actions={stageTransportActions}
                    {...cinemaTransportProps}
                />
            </div>
        </>
    )

    const playlistToggleButton = (source, className = styles.iconButton) =>
        track?.mediaKey ? (
            <WithTooltip
                asChild
                tip={nowPlayingInPlaylist ? 'Remove from playlist.' : 'Add to playlist.'}
            >
                <button
                    type="button"
                    className={cn(className, nowPlayingInPlaylist && styles.playlistButtonDone)}
                    onClick={() => handleTogglePlaylistMembership(source)}
                    aria-label={nowPlayingInPlaylist ? 'Remove from playlist' : 'Add to playlist'}
                >
                    {nowPlayingInPlaylist ? (
                        <Check aria-hidden="true" />
                    ) : (
                        <Plus aria-hidden="true" />
                    )}
                </button>
            </WithTooltip>
        ) : null

    const chatButton = (className = styles.iconButton) =>
        canOpenThread ? (
            <button
                type="button"
                className={className}
                onClick={handleShowInThread}
                aria-label="Chat"
                title="Chat — open thread"
            >
                <MessageCircle aria-hidden="true" />
            </button>
        ) : null

    // Volume icon with the VideoChrome-style vertical flyout. Clicking always
    // toggles mute; the slider pops on hover/focus (fine pointers only).
    const volumeControl = (
        <span className={styles.volumeControl} onMouseLeave={blurContainedActiveElement}>
            <button
                type="button"
                className={styles.iconButton}
                onClick={handleToggleMute}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                title={isMuted ? 'Unmute' : 'Mute'}
            >
                {isMuted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
            </button>
            <span className={styles.volumePopover}>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    className={styles.volumeFlyoutRange}
                    style={buildDockProgressStyle(volume * 100)}
                    onChange={handleVolume}
                    onInput={handleVolume}
                    aria-label="Volume"
                    aria-orientation="vertical"
                />
            </span>
        </span>
    )
    const isMobileDockViewport = typeof window !== 'undefined' && window.innerWidth <= 900
    const hostedPositionMode = isHostedInline ? hostedPlacement.positionMode : null
    // A frame that crops its artwork asks the parked video to crop the same way,
    // so the picture doesn't shrink inside black bars when playback starts.
    const hostedVideoCovers =
        isHostedInline && hostedPlacement.originEl?.dataset?.hostedMediaFit === 'cover'
    const hostedRootStyle = isHostedInline
        ? // Anchored, the position and size come from CSS; React must not write
          // inline values that would override them.
          hostedPositionMode === 'flow'
            ? HOSTED_OFFSCREEN_STYLE
            : hostedPositionMode === 'anchored'
            ? HOSTED_ANCHORED_RESET_STYLE
            : buildHostedPlacementStyle(
                  hostedGeometryRef.current?.rect,
                  hostedGeometryRef.current?.clip,
                  { borderRadius: hostedGeometryRef.current?.cornerRadii },
              ) || HOSTED_OFFSCREEN_STYLE
        : isHostedSilent
        ? HOSTED_OFFSCREEN_STYLE
        : null

    // The corner (and expanded) dock's measured offsets and height.
    const cornerRootStyle = {
        ...(dockBottom ? { bottom: dockBottom } : {}),
        ...(dockRight && !isExpanded && isMobileDockViewport
            ? {
                  right: dockRight,
                  maxWidth: `calc(100vw - ${dockRight + MOBILE_DOCK_MARGIN_PX}px)`,
              }
            : {}),
        ...(mobileDockPosition && !isExpanded && isMobileDockViewport
            ? {
                  left: mobileDockPosition.left,
                  top: mobileDockPosition.top,
                  right: 'auto',
                  bottom: 'auto',
              }
            : {}),
        // Fixed expanded height: switching tabs must not move the video; the
        // list scrolls inside instead.
        ...(isExpanded
            ? {
                  // Fixed elements ignore the native app's body-level safe-area
                  // padding, so the native apps (iOS, and Android's edge-to-edge
                  // webview) need that inset removed from the dock height as well.
                  height: getExpandedMediaDockHeight({
                      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
                      dockBottom: dockBottom || 130,
                      isMobileViewport: typeof window !== 'undefined' && window.innerWidth <= 900,
                      respectTopSafeArea: isIOS || isNativeAndroidApp(),
                  }),
              }
            : {}),
    }

    return (
        <div
            ref={rootRef}
            data-testid="media-dock"
            data-dock-state={
                isHostedSilent
                    ? 'hosted-silent'
                    : isHostedInline
                    ? 'hosted'
                    : isDockPreloading
                    ? 'preloading'
                    : isCinema
                    ? 'cinema'
                    : isExpanded
                    ? 'expanded'
                    : 'collapsed'
            }
            aria-hidden={isDockPreloading || isHostedSilent ? 'true' : undefined}
            role={isCinema ? 'dialog' : undefined}
            aria-modal={isCinema ? 'true' : undefined}
            aria-label={isCinema ? 'Cinema player' : undefined}
            className={cn(
                styles.dock,
                (isVideoMode || isCompactCard) && styles.dockVideoMode,
                // Preloading and presented states share one tree so the warmed
                // YouTube iframe survives the reveal; preloading only hides it.
                isDockPreloading && styles.dockPreloading,
                // Hosted: the same tree parked over the inline origin frame,
                // or held out of sight while the post owns the picture.
                isHostedInline && styles.dockHostedInline,
                hostedPositionMode === 'anchored' && styles.dockHostedAnchored,
                isHostedSilent && styles.dockHostedSilent,
                isExpanded && styles.dockExpanded,
                // Cinema: the expanded tree, with its stage grown to the window.
                isCinema && styles.dockCinema,
                !isVideoMode && !isCompactCard && !isDockPreloading && styles.dockAudio,
                isLanding && !isHostedInline && !isCinema && styles.hasLanded,
                dockAttention && styles.dockAttention,
                isDraggingDock && styles.dockDragging,
            )}
            style={hostedRootStyle || (isCinema ? CINEMA_ROOT_STYLE : cornerRootStyle)}
            data-no-modal-interaction
        >
            {!isExpanded && !isDockPreloading && !isHostedAttached ? (
                <button
                    type="button"
                    className={styles.dragHandle}
                    onPointerDown={handleDockPointerDown}
                    onPointerMove={handleDockPointerMove}
                    onPointerUp={handleDockPointerEnd}
                    onPointerCancel={handleDockPointerEnd}
                    onKeyDown={handleDockDragKeyDown}
                    aria-label="Move player"
                    title="Drag to move player"
                >
                    <GripVertical aria-hidden="true" />
                </button>
            ) : null}
            {isExpanded && !isCinema && !isDockPreloading ? (
                // The panel's top row: navigation, with collapse and close at
                // the right margin. No title row — the tile is the song.
                <div className={styles.tabBar}>
                    <button
                        type="button"
                        className={cn(
                            styles.tabButton,
                            activeTab === 'playlist' && styles.tabButtonActive,
                        )}
                        onClick={() => setActiveTab('playlist')}
                    >
                        <ListMusic aria-hidden="true" />
                        Playlist
                        {renderTabPlayingIcon('playlist')}
                        {playlistCount > 0 && (
                            <span className={styles.tabCount}>{playlistCount}</span>
                        )}
                    </button>
                    <button
                        type="button"
                        className={cn(
                            styles.tabButton,
                            activeTab === 'history' && styles.tabButtonActive,
                        )}
                        onClick={() => setActiveTab('history')}
                    >
                        <HistoryIcon aria-hidden="true" />
                        History
                        {renderTabPlayingIcon('history')}
                        {historyCount > 0 && (
                            <span className={styles.tabCount}>{historyCount}</span>
                        )}
                    </button>
                    {radioFeatureEnabled && (
                        <button
                            type="button"
                            className={cn(
                                styles.tabButton,
                                activeTab === 'radio' && styles.tabButtonActive,
                                radioEnabled && styles.tabButtonRadioOn,
                            )}
                            onClick={() => setActiveTab('radio')}
                        >
                            <Radio aria-hidden="true" />
                            Radio
                            {renderTabPlayingIcon('radio')}
                        </button>
                    )}
                    <span className={styles.tabBarActions}>
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={handleCollapse}
                            aria-label="Collapse player"
                        >
                            <ChevronDown aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={handleClose}
                            aria-label="Close player"
                        >
                            <X aria-hidden="true" />
                        </button>
                    </span>
                </div>
            ) : null}

            <span ref={mediaSurfaceHomeRef} hidden aria-hidden="true" />
            <div
                ref={mediaSurfaceRef}
                key="media-surface"
                className={cn(
                    styles.mediaSurface,
                    (showVideoSurface || isDockPreloading || isHostedInline) &&
                        !isHostedSilent &&
                        styles.mediaSurfaceVisible,
                    isExpanded && isVideoTakeover && !isDockPreloading && styles.stage,
                    isExpanded && isVideoTakeover && !isDockPreloading && styles.stageVideoSurface,
                    isCinema && isVideoTakeover && styles.stageCinema,
                    isCinema &&
                        isVideoTakeover &&
                        !showCinemaChrome &&
                        styles.stageCinemaChromeHidden,
                    hostedPositionMode === 'flow' && styles.mediaSurfaceHostedInOrigin,
                )}
                data-testid={
                    isExpanded && isVideoTakeover && !isDockPreloading
                        ? 'media-dock-stage'
                        : undefined
                }
                onClick={handleSurfaceClick}
                {...cinemaPointerIntentProps}
                onMouseEnter={handleSurfaceMouseEnter}
                onMouseLeave={handleSurfaceMouseLeave}
                onFocusCapture={handleSurfaceFocus}
                onBlurCapture={handleSurfaceBlur}
                tabIndex={isCompactCard && usesHoverVideoOverlay ? 0 : undefined}
                role={isCompactCard && usesHoverVideoOverlay ? 'group' : undefined}
                aria-label={
                    isCompactCard && usesHoverVideoOverlay
                        ? isVideoMode
                            ? 'Video controls'
                            : 'Audio controls'
                        : undefined
                }
            >
                {dockMediaTrack ? (
                    <DockMediaElement
                        key={dockMediaTrack.mediaKey}
                        ownerId={dockOwnerIdRef.current}
                        track={dockMediaTrack}
                        resumeToken={takeover?.resumeToken}
                        deferInitialOwnerPause={deferInitialOwnerPause}
                        handoffToken={takeover?.handoffToken ?? null}
                        preloadOnly={isDockPreloading}
                        preloadSyncToken={youtubePreloadSyncToken}
                        onYouTubePreloadStateChange={handleYouTubePreloadStateChange}
                        className={cn(styles.dockVideo, hostedVideoCovers && styles.dockVideoCover)}
                        onAutoplayBlocked={handleAutoplayBlocked}
                        onMediaError={handleDockMediaError}
                    />
                ) : isVideoMode && !isExpanded ? (
                    <div
                        className={styles.dockVideoPoster}
                        style={
                            track?.posterSrc
                                ? { backgroundImage: `url(${track.posterSrc})` }
                                : undefined
                        }
                        aria-hidden="true"
                    />
                ) : null}

                {isCompactAudio && !isHostedAttached ? (
                    // The combined-media card: the song's artwork (or the
                    // stage's gradient and note) with its title, under the
                    // same controls as a video frame. The audio element itself
                    // has no picture.
                    <div
                        className={cn(
                            styles.dockAudioCard,
                            showVideoOverlay && styles.dockAudioCardControlsShown,
                        )}
                        style={
                            displayArtwork
                                ? { backgroundImage: `url("${displayArtwork}")` }
                                : undefined
                        }
                        aria-hidden="true"
                    >
                        {!displayArtwork && <Music2 className={styles.dockAudioCardIcon} />}
                        <span className={styles.dockAudioCardTitle}>
                            {radioEnabled ? (
                                <Radio aria-hidden="true" />
                            ) : isPlaying ? (
                                <Equalizer />
                            ) : null}
                            <span className={styles.dockAudioCardTitleText}>{displayTitle}</span>
                        </span>
                    </div>
                ) : null}

                {isHostedInline ? (
                    // Inline surfaces portal their own controls here, above
                    // the hosted runtime.
                    <div
                        ref={setMediaDockHostedChromeLayer}
                        className={styles.hostedChromeLayer}
                        data-testid="media-dock-hosted-chrome"
                    />
                ) : null}

                {isExpanded && track && isVideoTakeover && !isDockPreloading
                    ? renderStageChrome({ showThreadHint: !isCinema })
                    : null}

                {isCompactCard && !isDockPreloading && !isHostedAttached ? (
                    <>
                        <div
                            className={cn(
                                styles.videoOverlay,
                                showVideoOverlay && styles.videoOverlayVisible,
                            )}
                            aria-hidden={!showVideoOverlay}
                        >
                            <button
                                type="button"
                                className={cn(styles.overlayButton, styles.overlayExpand)}
                                onClick={overlayAction(handleExpand)}
                                aria-label="Expand player"
                                tabIndex={showVideoOverlay ? 0 : -1}
                            >
                                <ChevronUp aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className={cn(styles.overlayButton, styles.overlayClose)}
                                onClick={event => {
                                    event.stopPropagation()
                                    handleClose()
                                }}
                                aria-label="Close player"
                                tabIndex={showVideoOverlay ? 0 : -1}
                            >
                                <X aria-hidden="true" />
                            </button>

                            <button
                                type="button"
                                className={cn(styles.overlayButton, styles.overlayPlay)}
                                onClick={overlayAction(() => handleTogglePlayback('overlay'))}
                                aria-label={
                                    isPlaying ? 'Pause' : radioEnabled ? 'Play music' : 'Play'
                                }
                                tabIndex={showVideoOverlay ? 0 : -1}
                            >
                                {isPlaying ? (
                                    <Pause aria-hidden="true" />
                                ) : radioEnabled ? (
                                    // The station, not a track: play reads as tuning in.
                                    <Radio aria-hidden="true" />
                                ) : (
                                    <Play className={styles.playIconOffset} aria-hidden="true" />
                                )}
                            </button>

                            {/* A live broadcast has no previous or next to step to. */}
                            {!radioEnabled && (
                                <>
                                    <button
                                        type="button"
                                        className={cn(styles.overlayButton, styles.overlayPrev)}
                                        disabled={!previousTrack}
                                        onClick={overlayAction(() => handleNavStep('prev'))}
                                        aria-label="Previous track"
                                        tabIndex={showVideoOverlay ? 0 : -1}
                                    >
                                        <SkipBack aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        className={cn(styles.overlayButton, styles.overlayNext)}
                                        disabled={!canStepNext}
                                        onClick={overlayAction(() => handleNavStep('next'))}
                                        aria-label="Next track"
                                        tabIndex={showVideoOverlay ? 0 : -1}
                                    >
                                        <SkipForward aria-hidden="true" />
                                    </button>
                                </>
                            )}

                            <button
                                type="button"
                                className={cn(
                                    styles.overlayButton,
                                    styles.overlayOutline,
                                    styles.overlayPlaylist,
                                    nowPlayingInPlaylist && styles.overlayPlaylistDone,
                                )}
                                onClick={overlayAction(() =>
                                    handleTogglePlaylistMembership('dock_video_overlay'),
                                )}
                                aria-label={
                                    nowPlayingInPlaylist
                                        ? 'Remove from playlist'
                                        : 'Add to playlist'
                                }
                                title={nowPlayingInPlaylist ? 'In playlist' : 'Add to playlist'}
                                tabIndex={showVideoOverlay ? 0 : -1}
                            >
                                {nowPlayingInPlaylist ? (
                                    <Check aria-hidden="true" />
                                ) : (
                                    <Plus aria-hidden="true" />
                                )}
                            </button>

                            {canOpenThread && (
                                <button
                                    type="button"
                                    className={cn(
                                        styles.overlayButton,
                                        styles.overlayOutline,
                                        styles.overlayChat,
                                    )}
                                    onClick={overlayAction(handleShowInThread)}
                                    aria-label="Chat"
                                    title="Chat — open thread"
                                    tabIndex={showVideoOverlay ? 0 : -1}
                                >
                                    <MessageCircle aria-hidden="true" />
                                </button>
                            )}

                            {hasAnswerControls && (
                                <span className={styles.overlayUpvalueSlot}>
                                    <QuickUpvalueButton
                                        resolveTarget={resolveUpvalueTarget}
                                        targetKey={nowPlayingKey}
                                        source="media_dock"
                                        authSource="media_dock_upvalue"
                                        variant="arrow"
                                        className={cn(styles.upvoteGlow, styles.upvalueNeonGreen)}
                                        iconSize={24}
                                        iconStrokeWidth={2.25}
                                        tabIndex={showVideoOverlay ? 0 : -1}
                                        onPress={pokeOverlay}
                                    />
                                </span>
                            )}
                        </div>

                        <MediaDockProgress
                            key={nowPlayingKey}
                            mediaKey={nowPlayingKey}
                            isPlaying={isPlaying}
                            isDocumentVisible={isDocumentVisible}
                            isExpanded={false}
                            variant="hairline"
                        />
                    </>
                ) : null}
            </div>

            {isExpanded && track && !isVideoTakeover ? (
                /* Artwork-backed tracks use the stage itself as the thread link.
                   Dock-owned video uses the same chrome on its live surface above. */
                <div
                    className={cn(
                        styles.stage,
                        canOpenThread && !isCinema && styles.stageClickable,
                        isCinema && styles.stageCinema,
                        isCinema && !showCinemaChrome && styles.stageCinemaChromeHidden,
                    )}
                    data-testid="media-dock-stage"
                    onClick={canOpenThread ? handleStageClick : undefined}
                    {...cinemaPointerIntentProps}
                    onFocusCapture={isCinema ? handleSurfaceFocus : undefined}
                    onBlurCapture={isCinema ? handleSurfaceBlur : undefined}
                    title={canOpenThread && !isCinema ? 'Open thread' : undefined}
                >
                    <div
                        className={styles.stageArtwork}
                        style={
                            displayArtwork
                                ? { backgroundImage: `url("${displayArtwork}")` }
                                : undefined
                        }
                        aria-hidden="true"
                    />
                    {!displayArtwork && (
                        <div className={styles.stagePlaceholder} aria-hidden="true">
                            {isVideoMode ? (
                                <Film className={styles.stagePlaceholderIcon} />
                            ) : (
                                <Music2 className={styles.stagePlaceholderIcon} />
                            )}
                        </div>
                    )}
                    {renderStageChrome({ showThreadHint: true })}
                </div>
            ) : null}

            {/* On the Radio tab the odds sit in its header, in the tuner's place. */}
            {isExpanded && !isCinema && track && !(liveRadioOdds && activeTab === 'radio') ? (
                <div className={styles.stageActions}>
                    {liveRadioOdds ? (
                        // A live broadcast has no previous or next to step to;
                        // show what put this song on the air instead.
                        liveRadioOddsBadge
                    ) : (
                        <>
                            <button
                                type="button"
                                className={styles.transportButton}
                                disabled={!previousTrack}
                                onClick={() => handleNavStep('prev')}
                                aria-label="Previous track"
                            >
                                <SkipBack aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className={styles.transportButton}
                                disabled={!canStepNext}
                                onClick={() => handleNavStep('next')}
                                aria-label="Next track"
                            >
                                <SkipForward aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>
            ) : null}

            {!isExpanded && !isCompactCard ? (
                <div className={styles.stack}>
                    <div className={styles.stackTitleRow}>
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={handleExpand}
                            aria-label="Expand player"
                        >
                            <ChevronUp aria-hidden="true" />
                        </button>
                        <span className={styles.stackTitle} title={displayTitle}>
                            {radioEnabled ? (
                                <Radio className={styles.titleNoteIcon} aria-hidden="true" />
                            ) : isPlaying ? (
                                <Equalizer />
                            ) : (
                                <Music2 className={styles.titleNoteIcon} aria-hidden="true" />
                            )}
                            <span className={styles.titleText}>{displayTitle}</span>
                        </span>
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={handleClose}
                            aria-label="Close player"
                        >
                            <X aria-hidden="true" />
                        </button>
                    </div>
                    <div className={styles.stackTransport}>
                        {playlistToggleButton(
                            'dock_collapsed',
                            cn(styles.iconButton, styles.stackActionButton),
                        )}
                        {playPauseButton(styles.stackPlayButton, styles.stackPlayIcon, 'row')}
                        {chatButton(cn(styles.iconButton, styles.stackActionButton))}
                        {signalNeon}
                    </div>
                    <div className={styles.stackSeek}>
                        <MediaDockProgress
                            key={nowPlayingKey}
                            mediaKey={nowPlayingKey}
                            isPlaying={isPlaying}
                            isDocumentVisible={isDocumentVisible}
                            isExpanded={false}
                            onDraggingChange={setIsDraggingSeek}
                        />
                        {volumeControl}
                    </div>
                </div>
            ) : null}

            {isExpanded && !isCinema ? (
                <div
                    className={cn(
                        styles.historySection,
                        activeTab === 'radio' && styles.historySectionRadio,
                    )}
                >
                    {activeTab === 'radio' ? (
                        <>
                            <div className={styles.radioHeaderRow}>
                                <span className={styles.radioHeadline}>
                                    <span className={styles.radioTitle}>Treechat Radio</span>
                                    <span className={styles.radioTagline}>
                                        One live broadcast of the most upvalued songs — everyone
                                        hears the same thing.
                                    </span>
                                    {radioEnabled && radioUpNextTitle && (
                                        <span className={styles.radioUpNext}>
                                            Up next: {radioUpNextTitle}
                                        </span>
                                    )}
                                </span>
                                <div className={styles.radioControls}>
                                    {liveRadioOddsBadge || (
                                        <button
                                            type="button"
                                            className={cn(
                                                styles.radioToggle,
                                                radioEnabled && styles.radioToggleOn,
                                            )}
                                            onClick={() =>
                                                radioEnabled
                                                    ? stopRadio('radio_tab')
                                                    : startRadio('radio_tab')
                                            }
                                            aria-pressed={radioEnabled}
                                        >
                                            <Radio aria-hidden="true" />
                                            {radioEnabled ? 'On air' : 'Tune in'}
                                        </button>
                                    )}
                                    {/* Broadcasting plays is a radio behavior; it lives with the tuner. */}
                                    {radioFeatureEnabled && (
                                        <button
                                            type="button"
                                            className={cn(
                                                styles.modeButton,
                                                broadcastPlays && styles.modeButtonActive,
                                            )}
                                            onClick={toggleBroadcastPlays}
                                            aria-label={
                                                broadcastPlays
                                                    ? 'Stop broadcasting plays'
                                                    : 'Broadcast plays'
                                            }
                                            aria-pressed={broadcastPlays}
                                            title={
                                                broadcastPlays
                                                    ? 'Broadcasting plays: upvalues you send while a song plays post "@you is playing …" and back the song on Treechat Music'
                                                    : 'Play broadcasting off: your upvalues stay quiet about what you are listening to'
                                            }
                                        >
                                            <Megaphone aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <RadioTrackList
                                pool={radioPool}
                                nowPlayingKey={nowPlayingKey}
                                isPlaying={isPlaying}
                                trackAnswerTitles={trackAnswerTitles}
                                trackAnswerArtwork={trackAnswerArtwork}
                                formatSats={formatSatsAmount}
                            />
                        </>
                    ) : (
                        <>
                            {activeTab === 'playlist' && (
                                // Playlist modes belong to the playlist, so
                                // they sit over it rather than over every tab.
                                <div className={styles.listToolbar}>
                                    <button
                                        type="button"
                                        className={cn(
                                            styles.modeButton,
                                            loopEnabled && styles.modeButtonActive,
                                        )}
                                        onClick={() => togglePlaybackMode('loop')}
                                        aria-label={
                                            loopEnabled ? 'Disable playlist loop' : 'Loop playlist'
                                        }
                                        aria-pressed={loopEnabled}
                                        title={loopEnabled ? 'Loop on' : 'Loop off'}
                                    >
                                        <Repeat aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        className={cn(
                                            styles.modeButton,
                                            shuffleEnabled && styles.modeButtonActive,
                                        )}
                                        onClick={() => togglePlaybackMode('shuffle')}
                                        aria-label={
                                            shuffleEnabled
                                                ? 'Disable playlist shuffle'
                                                : 'Shuffle playlist'
                                        }
                                        aria-pressed={shuffleEnabled}
                                        title={shuffleEnabled ? 'Shuffle on' : 'Shuffle off'}
                                    >
                                        <Shuffle aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                            <DockTrackList
                                items={activeTab === 'playlist' ? playlist : history}
                                tab={activeTab === 'playlist' ? 'playlist' : 'history'}
                                nowPlayingKey={nowPlayingKey}
                                isPlaying={isPlaying}
                                trackAnswerTitles={trackAnswerTitles}
                                trackAnswerArtwork={trackAnswerArtwork}
                                onTogglePlayback={handleTogglePlayback}
                                onPlayTrack={playTrack}
                                onHistoryChanged={handleHistoryChanged}
                                hasMore={
                                    activeTab === 'playlist'
                                        ? playlistPagination.hasMore
                                        : historyPagination.hasMore
                                }
                                isLoading={
                                    activeTab === 'playlist'
                                        ? playlistPagination.isLoading
                                        : historyPagination.isLoading
                                }
                                onLoadMore={loadMoreMediaDockItems}
                            />
                        </>
                    )}
                </div>
            ) : null}
        </div>
    )
}
